<template>
  <div>
    <div class="flex gap-0 bg-slate-100 max-md:flex-wrap">
      <div class="flex flex-col flex-1 max-md:max-w-full">
        <div class="flex flex-col items-start self-center pt-7 pr-20 pb-12 pl-6 mt-8 w-full bg-white rounded-3xl max-w-[1285px] max-md:px-5 max-md:max-w-full">
        <!-- Header -->
        <div class="flex gap-4 text-2xl font-medium whitespace-nowrap text-neutral-800 mb-6">
          <img
            loading="lazy"
            src="@/assets/img/brand.svg"
            class="aspect-[0.95] w-[21px]"
          />
          <div class="grow text-xs sm:text-sm md:text-[22px]">
            Landing Page Configuration
          </div>
        </div>

        <!-- Location Selector -->
        <div class="w-full mb-8">
          <label class="block text-sm font-medium text-gray-700 mb-2">Select Location</label>
          <div class="relative">
            <select 
              v-model="selectedLocationId" 
              @change="onLocationChange" 
              class="w-full px-4 py-3 pr-10 border-2 border-gray-400 rounded-lg focus:ring-1 focus:ring-blue-400 focus:border-blue-400 bg-white shadow-sm hover:border-gray-500 transition-colors duration-200 cursor-pointer appearance-none"
              :disabled="locations.length === 0"
            >
              <option v-if="locations.length === 0" value="" disabled>
                No locations found. Please add locations to your workspace first.
              </option>
              <option v-for="loc in locations" :key="loc.id" :value="loc.id">
                {{ loc.name || loc.storeLocation || loc.id }}
              </option>
            </select>
            <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
          </div>
          <div v-if="locations.length === 0" class="mt-2 text-sm text-red-600">
            No locations found. Please add locations to your workspace before configuring individual store pages.
          </div>
        </div>

        <!-- Preview Section -->
        <div class="w-full mb-8">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-gray-800">Live Preview</h3>
            <button
              @click="refreshPreview"
              :disabled="isRefreshingPreview"
              class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
              style="flex: 0 0 auto;"
            >
              <svg v-if="isRefreshingPreview" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              {{ isRefreshingPreview ? 'Refreshing...' : 'Refresh Preview' }}
            </button>
          </div>
          <p class="text-sm text-gray-600 mb-4">The preview below shows how your landing page will appear with the current configuration. Changes are applied automatically as you configure the settings.</p>
          <div class="border-2 border-gray-200 rounded-lg overflow-auto" style="height: 600px;">
            <IndividualStore
              v-if="selectedLocationId && locations.length > 0"
              ref="individualStoreComponent"
              :userUid="currentUID"
              :wid="WID"
              :locationId="selectedLocationId"
              :slug="getSlugForLocation(selectedLocation)"
              :disableTitleAndFavicon="true"
              :draftLocationStyle="locationStyleDraft"
              :draftCtaAppointments="ctaAppointmentsDraft"
              :draftHeaderStyle="headerStyleDraft"
              :draftHeroSection="heroSectionDraft"
              :draftReviewsSection="reviewsSectionDraft"
              :draftContactInformation="contactInformationDraft"
              :draftAppointmentStyles="appointmentStylesDraft"
              :draftProductStyles="productStylesDraft"
              :draftPostsSection="postsSectionDraft"
              :draftBlogSection="blogSectionDraft"
              :draftGallerySection="gallerySectionDraft"
              :draftFooterSection="footerSectionDraft"
              :draftSocialSection="socialSectionDraft"
              :draftSectionHeaders="sectionHeadersDraft"
              :key="storeRefreshKey"
            />
            <div v-else-if="locations.length === 0" class="flex items-center justify-center h-full text-gray-400">
              <div class="text-center">
                <svg class="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                </svg>
                <p class="text-lg font-medium text-gray-500 mb-2">No Locations Found</p>
                <p class="text-sm text-gray-400">Add locations to your workspace to configure landing pages.</p>
              </div>
            </div>
            <div v-else class="flex items-center justify-center h-full text-gray-400">
              <div class="text-center">
                <svg class="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <p class="text-lg font-medium text-gray-500 mb-2">Select a Location</p>
                <p class="text-sm text-gray-400">Choose a location from the dropdown above to preview the landing page.</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Configuration Section -->
        <div class="w-full">
          <h3 class="text-lg font-semibold text-gray-800 mb-4">Configuration Options</h3>
          

          
          <!-- Tab Navigation -->
          <div class="mb-6">
            <!-- Mobile/Tablet: Dropdown Select -->
            <div class="md:hidden">
              <label class="block text-sm font-medium text-gray-700 mb-2">Configuration Section</label>
              <select 
                v-model="activeTab" 
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
              >
                <option v-for="tab in configurationTabs" :key="tab.id" :value="tab.id">
                  {{ tab.label }}
                </option>
              </select>
            </div>
            
            <!-- Desktop: Horizontal Tabs with Overflow Handling -->
            <div class="hidden md:block">
              <div class="relative">
                <!-- Left Arrow Button -->
                <button
                  v-if="canScrollLeft"
                  @click="scrollTabs('left')"
                  class="absolute left-0 top-0 z-10 h-full px-2 bg-gradient-to-r from-white via-white to-transparent flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                  </svg>
                </button>
                
                <!-- Right Arrow Button -->
                <button
                  v-if="canScrollRight"
                  @click="scrollTabs('right')"
                  class="absolute right-0 top-0 z-10 h-full px-2 bg-gradient-to-l from-white via-white to-transparent flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </button>
                
                <!-- Tabs Container -->
                <div 
                  ref="tabsContainer"
                  class="flex border-b border-gray-200 overflow-x-auto scrollbar-hide"
                  @scroll="checkScrollButtons"
                >
                  <div class="flex min-w-max">
                    <button
                      v-for="tab in configurationTabs"
                      :key="tab.id"
                      @click="activeTab = tab.id"
                      :class="[
                        'px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      ]"
                    >
                      {{ tab.label }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Tab Content -->
          <div class="min-h-[600px]">
            <!-- Appointments & CTA Tab -->
            <div v-if="activeTab === 'appointments-cta'" class="space-y-8">
              
              <!-- Appointments & CTA Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  Appointments & CTA Configuration
                </h4>
                
                <!-- Individual Store Appointment Settings -->
                <div class="mb-8">
                  <h5 class="text-md font-semibold text-gray-700 mb-4 flex items-center">
                    <svg class="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                    Individual Store Appointment Settings
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div v-for="(field, key) in locationStyleFields" :key="key" class="space-y-2">
                      <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                      <div class="flex items-center gap-3">
                        <input
                          v-if="field.type === 'color'"
                          type="color"
                          v-model="locationStyleDraft[key]"
                          class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        />
                        <input
                          v-else-if="field.type === 'string'"
                          type="text"
                          v-model="locationStyleDraft[key]"
                          :placeholder="field.placeholder"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <input
                          v-else-if="field.type === 'boolean'"
                          type="checkbox"
                          v-model="locationStyleDraft[key]"
                          class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>
                      <p class="text-xs text-gray-500">{{ field.description }}</p>
                    </div>
                  </div>
                </div>

                <!-- Workspace-Level Appointment & CTA Settings -->
                <div class="mb-8">
                  <h5 class="text-md font-semibold text-gray-700 mb-4 flex items-center">
                    <svg class="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                    </svg>
                    Workspace-Level Settings (Applies to All Stores)
                  </h5>
                  


                  <!-- CTA Configuration Section -->
                  <div class="mb-6">
                    <h6 class="text-sm font-semibold text-gray-600 mb-3 flex items-center">
                      <svg class="w-3 h-3 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                      </svg>
                      Header Call-to-Action Buttons
                    </h6>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div v-for="(field, key) in ctaAppointmentsFields" :key="key" class="space-y-2" v-if="key !== 'appointments'">
                        <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                        
                        <!-- Boolean fields (checkboxes) -->
                        <div v-if="field.type === 'boolean'" class="flex items-center">
                          <input
                            type="checkbox"
                            v-model="ctaAppointmentsDraft[key]"
                            :disabled="key === 'showAppointmentButton' && !locationStyleDraft.appointments"
                            :class="[
                              'w-4 h-4 border-gray-300 rounded focus:ring-2',
                              key === 'showAppointmentButton' && !locationStyleDraft.appointments
                                ? 'text-gray-400 bg-gray-200 cursor-not-allowed'
                                : 'text-blue-600 bg-gray-100 focus:ring-blue-500'
                            ]"
                          />
                          <span 
                            :class="[
                              'ml-2 text-sm',
                              key === 'showAppointmentButton' && !locationStyleDraft.appointments
                                ? 'text-gray-400'
                                : 'text-gray-600'
                            ]"
                          >
                            {{ field.description }}
                            <span v-if="key === 'showAppointmentButton' && !locationStyleDraft.appointments" class="text-xs text-gray-500 block mt-1">
                              (Disabled because appointment booking is turned off for this store)
                            </span>
                          </span>
                        </div>
                        
                        <!-- Regular text input fields -->
                        <div v-else class="flex items-center gap-3">
                          <input
                            type="text"
                            v-model="ctaAppointmentsDraft[key]"
                            :placeholder="field.placeholder"
                            class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        
                        <p v-if="field.type !== 'boolean'" class="text-xs text-gray-500">{{ field.description }}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Bulk Actions Section -->
                <div class="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div class="mb-4">
                    <h5 class="text-sm font-semibold text-blue-800 mb-1">Bulk Actions - Edit All Store Appointments</h5>
                    <p class="text-xs text-blue-600">
                      Enable or disable appointment booking for all locations in this workspace simultaneously
                    </p>
                  </div>
                  <div class="flex gap-3">
                    <button
                      @click="enableAppointmentsForAllLocations"
                      :disabled="isEnablingAllAppointments || isDisablingAllAppointments"
                      class="flex-1 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg v-if="isEnablingAllAppointments" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                      </svg>
                      <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      {{ isEnablingAllAppointments ? 'Enabling...' : 'Enable All' }}
                    </button>
                    <button
                      @click="disableAppointmentsForAllLocations"
                      :disabled="isEnablingAllAppointments || isDisablingAllAppointments"
                      class="flex-1 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg v-if="isDisablingAllAppointments" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                      </svg>
                      <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                      {{ isDisablingAllAppointments ? 'Disabling...' : 'Disable All' }}
                    </button>
                  </div>
                </div>

                <!-- Help Section -->
                <div class="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h6 class="text-sm font-semibold text-blue-800 mb-2">💡 Configuration Tips</h6>
                  <ul class="text-xs text-blue-700 space-y-1">
                    <li>• <strong>Individual Store Settings:</strong> Control appointment booking for this specific store</li>
                    <li>• <strong>Workspace Settings:</strong> Configure CTA buttons that apply to all stores in your workspace</li>
                    <li>• <strong>Show both buttons:</strong> Display appointment and call buttons together</li>
                    <li>• <strong>Show only one:</strong> Choose either appointment or call button to avoid clutter</li>
                    <li>• <strong>Custom text:</strong> Use "{{phone}}" placeholder in call button text to show actual phone number</li>
                    <li>• <strong>Examples:</strong> "Book Now", "Schedule Appointment", "Call {{phone}}", "Get Quote"</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Products & Services Tab -->
            <div v-if="activeTab === 'products-services'" class="space-y-8">
              
              <!-- Products & Services Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                  </svg>
                  Products & Services Management
                </h4>
                
                <!-- Import/Export Section -->
                <div class="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <h5 class="text-md font-semibold text-gray-800 mb-3">Import/Export Products & Services</h5>
                  
                  <!-- Import/Export Row -->
                  <div class="mb-4">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <!-- CSV Import -->
                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">Import from CSV</label>
                        <input
                          ref="csvFileInput"
                          type="file"
                          accept=".csv"
                          @change="handleCsvImport"
                          class="hidden"
                        />
                        <button
                          @click="$refs.csvFileInput.click()"
                          class="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 font-medium flex items-center gap-2"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                          </svg>
                          Choose CSV File
                        </button>
                      </div>

                      <!-- CSV Export -->
                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">Export to CSV</label>
                        <button
                          @click="exportToCsv"
                          :disabled="productsServicesDraft.length === 0"
                          class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium flex items-center gap-2"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                          </svg>
                          Export Current Data
                        </button>
                      </div>
                    </div>
                    
                    <!-- Status Message -->
                    <div v-if="csvImportStatus" class="mt-2">
                      <span class="text-sm" :class="csvImportStatus.type === 'success' ? 'text-green-600' : 'text-red-600'">
                        {{ csvImportStatus.message }}
                      </span>
                    </div>
                    
                    <p class="text-xs text-gray-500 mt-2">
                      CSV should have columns: name, description, unitPrice, images, videos
                    </p>
                  </div>

                  <!-- CSV Instructions -->
                  <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h6 class="text-sm font-semibold text-blue-800 mb-2">📋 How to Upload CSV File</h6>
                    <div class="text-xs text-blue-700 space-y-1">
                      <p><strong>Step 1:</strong> Download the template below</p>
                      <p><strong>Step 2:</strong> Open it in Excel, Google Sheets, or any spreadsheet app</p>
                      <p><strong>Step 3:</strong> Fill in your products/services data:</p>
                      <ul class="ml-4 mt-1 space-y-1">
                        <li>• <strong>name:</strong> Product/service name</li>
                        <li>• <strong>description:</strong> Brief description</li>
                        <li>• <strong>unitPrice:</strong> Price (e.g., "$50", "Free")</li>
                        <li>• <strong>images:</strong> Image URLs separated by | (pipe)</li>
                        <li>• <strong>videos:</strong> Video URLs separated by | (pipe)</li>
                      </ul>
                      <p><strong>Step 4:</strong> Save as CSV file and upload using "Choose CSV File" button above</p>
                    </div>
                  </div>

                  <!-- CSV Template Download -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Get CSV Template Sample</label>
                    <button
                      @click="downloadCsvTemplate"
                      class="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 font-medium flex items-center gap-2"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                      Download Template
                    </button>
                  </div>
                </div>

                <!-- Add New Product/Service Button -->
                <div class="mb-6">
                  <button
                    @click="addNewProductService"
                    class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium flex items-center gap-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    Add New Product/Service
                  </button>
                </div>

                <!-- Products/Services List -->
                <div v-if="productsServicesDraft.length === 0" class="text-center py-8 text-gray-500">
                  <svg class="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                  </svg>
                  <p class="text-lg font-medium mb-2">No Products/Services Added</p>
                  <p class="text-sm">Click "Add New Product/Service" to get started</p>
                </div>

                <div v-else class="space-y-6">
                  <div 
                    v-for="(item, index) in productsServicesDraft" 
                    :key="index"
                    :class="[
                      'border border-gray-200 rounded-lg p-6 bg-gray-50 relative transition-all duration-200',
                      draggedIndex === index ? 'opacity-50 shadow-lg' : 'hover:shadow-md'
                    ]"
                    draggable="true"
                    @dragstart="handleDragStart(index, $event)"
                    @dragover.prevent
                    @drop="handleDrop(index, $event)"
                    @dragenter.prevent
                  >
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <!-- Left Column: Title and Order Badge -->
                      <div class="flex items-center gap-3">
                        <span class="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                          #{{ index + 1 }}
                        </span>
                        <h5 class="text-lg font-semibold text-gray-800">Product/Service</h5>
                      </div>
                      <!-- Right Column: Controls -->
                      <div class="flex items-center justify-end gap-2">
                        <!-- Move Up/Down Buttons -->
                        <button
                          @click="moveProductUp(index)"
                          :disabled="index === 0"
                          class="p-1 text-gray-400 hover:text-gray-600 disabled:text-gray-200 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
                          </svg>
                        </button>
                        <button
                          @click="moveProductDown(index)"
                          :disabled="index === productsServicesDraft.length - 1"
                          class="p-1 text-gray-400 hover:text-gray-600 disabled:text-gray-200 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </button>
                        <!-- Drag Handle -->
                        <div class="w-4 h-4 cursor-move text-gray-400 hover:text-gray-600">
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <!-- Name -->
                      <div class="space-y-2">
                        <label class="block text-sm font-medium text-gray-700">Name *</label>
                        <input
                          v-model="item.name"
                          type="text"
                          placeholder="Enter product/service name"
                          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>

                      <!-- Unit Price -->
                      <div class="space-y-2">
                        <label class="block text-sm font-medium text-gray-700">Unit Price</label>
                        <input
                          v-model="item.unitPrice"
                          type="text"
                          placeholder="e.g., $50, $100/hour, Free"
                          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <!-- Description -->
                      <div class="space-y-2 md:col-span-2">
                        <label class="block text-sm font-medium text-gray-700">Description</label>
                        <textarea
                          v-model="item.description"
                          rows="3"
                          placeholder="Describe your product or service..."
                          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        ></textarea>
                      </div>

                      <!-- Images -->
                      <div class="space-y-2 md:col-span-2">
                        <label class="block text-sm font-medium text-gray-700">Images</label>
                        <div class="space-y-2">
                          <!-- Existing Image Links -->
                          <div 
                            v-for="(image, imageIndex) in item.images" 
                            :key="imageIndex"
                            class="flex gap-2"
                          >
                            <input
                              v-model="item.images[imageIndex]"
                              type="url"
                              placeholder="https://example.com/image.jpg"
                              class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <button
                              @click="removeImage(index, imageIndex)"
                              class="px-3 py-2 text-red-500 hover:text-red-700 border border-red-300 rounded-md hover:bg-red-50 flex-shrink-0"
                            >
                              Remove
                            </button>
                          </div>
                          
                          <!-- Image Upload Button -->
                          <div class="flex flex-wrap gap-2">
                            <button
                              @click="initCloudinaryImageUpload(index)"
                              :disabled="isUploadingImage"
                              class="px-3 py-2 text-blue-500 hover:text-blue-700 border border-blue-300 rounded-md hover:bg-blue-50 text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 whitespace-nowrap"
                            >
                              <svg v-if="isUploadingImage" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                              </svg>
                              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                              </svg>
                              {{ isUploadingImage ? 'Uploading...' : 'Upload Image' }}
                            </button>
                            <button
                              @click="addImage(index)"
                              class="px-3 py-2 text-green-500 hover:text-green-700 border border-green-300 rounded-md hover:bg-green-50 text-sm flex-shrink-0 whitespace-nowrap"
                            >
                              + Add Image Link
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Videos -->
                      <div class="space-y-2 md:col-span-2">
                        <label class="block text-sm font-medium text-gray-700">Videos</label>
                        <div class="space-y-2">
                          <!-- Existing Video Links -->
                          <div 
                            v-for="(video, videoIndex) in item.videos" 
                            :key="videoIndex"
                            class="flex gap-2"
                          >
                            <input
                              v-model="item.videos[videoIndex]"
                              type="url"
                              placeholder="https://example.com/video.mp4 or YouTube URL"
                              class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <button
                              @click="removeVideo(index, videoIndex)"
                              class="px-3 py-2 text-red-500 hover:text-red-700 border border-red-300 rounded-md hover:bg-red-50 flex-shrink-0"
                            >
                              Remove
                            </button>

                          </div>
                          
                          <!-- Video Upload Button -->
                          <div class="flex flex-wrap gap-2">
                            <button
                              @click="uploadVideoToCloudinaryWidget(index)"
                              :disabled="isUploadingVideo"
                              class="px-3 py-2 text-purple-500 hover:text-purple-700 border border-purple-300 rounded-md hover:bg-purple-50 text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 whitespace-nowrap"
                            >
                              <svg v-if="isUploadingVideo" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                              </svg>
                              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                              </svg>
                              {{ isUploadingVideo ? 'Uploading...' : 'Upload Video' }}
                            </button>
                            <button
                              @click="addVideo(index)"
                              class="px-3 py-2 text-green-500 hover:text-green-700 border border-green-300 rounded-md hover:bg-green-50 text-sm flex-shrink-0 whitespace-nowrap"
                            >
                              + Add Video Link
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Hidden file input for video upload -->
                      <input
                        :ref="`videoFileInput_${index}`"
                        type="file"
                        accept="video/*"
                        style="display: none;"
                        @change="handleVideoFileUpload($event, index)"
                        :data-index="index"
                      />
                    </div>
                    
                    <!-- Delete Button - Bottom Right -->
                    <div class="flex justify-end mt-4 pt-4 border-t border-gray-200">
                      <button
                        @click="removeProductService(index)"
                        class="px-4 py-2 text-red-600 hover:text-red-700 border border-red-300 rounded-md hover:bg-red-50 flex items-center gap-2 transition-colors"
                        title="Remove this product/service"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                        Delete Product/Service
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Help Section -->
                <div class="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h6 class="text-sm font-semibold text-blue-800 mb-2">💡 Products & Services Tips</h6>
                  <ul class="text-xs text-blue-700 space-y-1">
                    <li>• <strong>Name:</strong> Keep it clear and descriptive</li>
                    <li>• <strong>Unit Price:</strong> Use formats like "$50", "$100/hour", "Free", "Contact for quote"</li>
                    <li>• <strong>Description:</strong> Explain what you offer and its benefits</li>
                    <li>• <strong>Images:</strong> Add direct links to product/service images (JPG, PNG, etc.)</li>
                    <li>• <strong>Videos:</strong> Add links to videos (MP4, YouTube, Vimeo, etc.)</li>
                    <li>• <strong>Organization:</strong> List your most important products/services first</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Header Styles Tab -->
            <div v-if="activeTab === 'header'" class="space-y-8">
              
              <!-- Header Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                  Header Styling
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in headerStyleFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    
                    <!-- Boolean fields (checkboxes) -->
                    <div v-if="field.type === 'boolean'" class="flex items-center">
                      <input
                        type="checkbox"
                        v-model="headerStyleDraft[key]"
                        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <span class="ml-2 text-sm text-gray-600">{{ field.description }}</span>
                    </div>
                    
                    <!-- Color input fields -->
                    <div v-else-if="field.type === 'color'" class="flex items-center gap-3">
                      <input
                        type="color"
                        v-model="headerStyleDraft[key]"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        type="text"
                        v-model="headerStyleDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <!-- Regular text input fields -->
                    <div v-else class="flex items-center gap-3">
                      <input
                        type="text"
                        v-model="headerStyleDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <p v-if="field.type !== 'boolean'" class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>



            <!-- Hero Section Tab -->
            <div v-if="activeTab === 'hero'" class="space-y-8">
              
              <!-- Hero Section Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  </svg>
                  Hero Section
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in heroSectionFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    <div class="flex items-center gap-3">
                      <input
                        v-if="field.type === 'color'"
                        type="color"
                        v-model="heroSectionDraft[key]"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        v-else
                        type="text"
                        v-model="heroSectionDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>



            <!-- Reviews Section Tab -->
            <div v-if="activeTab === 'reviews'" class="space-y-8">
              
              <!-- Reviews Section Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                  Reviews Section
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in reviewsSectionFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    <div class="flex items-center gap-3">
                      <input
                        v-if="field.type === 'color'"
                        type="color"
                        v-model="reviewsSectionDraft[key]"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        v-else
                        type="text"
                        v-model="reviewsSectionDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>



            <!-- Contact Information Tab -->
            <div v-if="activeTab === 'contact'" class="space-y-8">
              
              <!-- Contact Information Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                  Contact Information
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in contactInformationFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    <div class="flex items-center gap-3">
                      <input
                        v-if="field.type === 'color'"
                        type="color"
                        v-model="contactInformationDraft[key]"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        v-else
                        type="text"
                        v-model="contactInformationDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>



            <!-- Appointment Styles Tab -->
            <div v-if="activeTab === 'appointments'" class="space-y-8">
              
              <!-- Appointment Styles Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                  Appointment Styles
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in appointmentStylesFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    <div class="flex items-center gap-3">
                      <input
                        v-if="field.type === 'color'"
                        type="color"
                        v-model="appointmentStylesDraft[key]"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        v-else
                        type="text"
                        v-model="appointmentStylesDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>



            <!-- Product Styles Tab -->
            <div v-if="activeTab === 'product-styles'" class="space-y-8">
              
              <!-- Product Styles Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
                  </svg>
                  Product & Services Styling
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in productStylesFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    <div class="flex items-center gap-3">
                      <input
                        v-if="field.type === 'color'"
                        type="color"
                        v-model="productStylesDraft[key]"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        v-else
                        type="text"
                        v-model="productStylesDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>



            <!-- Posts Section Tab -->
            <div v-if="activeTab === 'posts'" class="space-y-8">
              
              <!-- Posts Section Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                  </svg>
                  Posts Section Styling
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in postsSectionFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    <div class="flex items-center gap-3">
                      <input
                        v-if="field.type === 'color'"
                        type="color"
                        v-model="postsSectionDraft[key]"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        v-else
                        type="text"
                        v-model="postsSectionDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>



            <!-- Blog Section Tab -->
            <div v-if="activeTab === 'blogs'" class="space-y-8">
              
              <!-- Blog Section Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                  </svg>
                  Blog Section Styling
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in blogSectionFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    <div class="flex items-center gap-3">
                      <input
                        v-if="field.type === 'color'"
                        type="color"
                        v-model="blogSectionDraft[key]"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        v-else
                        type="text"
                        v-model="blogSectionDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>



            <!-- Gallery Section Tab -->
            <div v-if="activeTab === 'gallery'" class="space-y-8">
              
              <!-- Gallery Section Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  Gallery Section Styling
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in gallerySectionFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    <div class="flex items-center gap-3">
                      <input
                        v-if="field.type === 'color'"
                        type="color"
                        v-model="gallerySectionDraft[key]"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        v-else
                        type="text"
                        v-model="gallerySectionDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>



            <!-- Footer Section Tab -->
            <div v-if="activeTab === 'footer'" class="space-y-8">
              
              <!-- Footer Section Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                  Footer Section Styling
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in footerSectionFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    <div class="flex items-center gap-3">
                      <input
                        v-if="field.type === 'color'"
                        type="color"
                        v-model="footerSectionDraft[key]"
                        class="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                      />
                      <input
                        v-else
                        type="text"
                        v-model="footerSectionDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section Headers Tab -->
            <div v-if="activeTab === 'section-headers'" class="space-y-8">
              
              <!-- Section Headers Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"></path>
                  </svg>
                  Section Headers Configuration
                </h4>
                
                <p class="text-sm text-gray-600 mb-6">
                  Customize the titles and subtext for each section of your landing page. These will be displayed as the main headings and descriptions for each section.
                </p>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div v-for="(field, key) in sectionHeadersFields" :key="key" class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">{{ field.label }}</label>
                    <div class="flex items-center gap-3">
                      <input
                        type="text"
                        v-model="sectionHeadersDraft[key]"
                        :placeholder="field.placeholder"
                        class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <p class="text-xs text-gray-500">{{ field.description }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Themes Tab -->
            <div v-if="activeTab === 'themes'" class="space-y-8">
              
              <!-- Themes Configuration -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"></path>
                  </svg>
                  Choose a Theme
                </h4>
                
                <p class="text-sm text-gray-600 mb-6">
                  Select a predefined theme to instantly apply a cohesive color scheme and styling to all your store landing pages.
                </p>
                
                <!-- Theme Grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div
                    v-for="(theme, themeKey) in predefinedThemes"
                    :key="themeKey"
                    @click="selectTheme(themeKey)"
                    :class="[
                      'relative p-6 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-lg',
                      currentTheme === themeKey
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    ]"
                  >
                    <!-- Selected Indicator -->
                    <div
                      v-if="currentTheme === themeKey"
                      class="absolute top-3 right-3 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center"
                    >
                      <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    
                    <!-- Theme Preview -->
                    <div class="flex items-center gap-4 mb-4">
                      <div
                        class="w-12 h-12 rounded-lg shadow-sm"
                        :style="{ backgroundColor: theme.preview }"
                      ></div>
                      <div>
                        <h5 class="font-semibold text-gray-800">{{ theme.name }}</h5>
                        <p class="text-sm text-gray-600">{{ theme.description }}</p>
                      </div>
                    </div>
                    
                    <!-- Color Preview -->
                    <div class="grid grid-cols-5 gap-2 mb-4">
                      <div
                        v-for="(value, key) in Object.entries(theme.variables).slice(0, 5)"
                        :key="key"
                        class="w-6 h-6 rounded border border-gray-200"
                        :style="{ backgroundColor: value[1] }"
                        :title="`${key}: ${value[1]}`"
                      ></div>
                    </div>
                    
                    <!-- Apply Button -->
                    <button
                      @click.stop="applyTheme(themeKey)"
                      :class="[
                        'w-full py-2 px-4 rounded-md text-sm font-medium transition-colors',
                        currentTheme === themeKey
                          ? 'bg-blue-500 text-white hover:bg-blue-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      ]"
                    >
                      {{ currentTheme === themeKey ? 'Currently Applied' : 'Apply Theme' }}
                    </button>
                  </div>
                </div>
                
                <!-- Custom Theme Section -->
                <div class="mt-8 pt-6 border-t border-gray-200">
                  <h5 class="text-lg font-semibold text-gray-800 mb-4">Custom Theme</h5>
                  <p class="text-sm text-gray-600 mb-4">
                    Create a custom theme by manually adjusting individual color values in the other configuration tabs.
                  </p>
                  <div class="flex items-center gap-4">
                    <button
                      @click="resetToCustomTheme"
                      class="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 font-medium"
                    >
                      Reset to Custom
                    </button>
                    <span class="text-sm text-gray-500">
                      This will clear the current theme and allow manual customization
                    </span>
                  </div>
                </div>
              </div>
            </div>







            <!-- Domain Management Tab -->
            <div v-if="activeTab === 'domain-management'" class="space-y-8">
              <!-- Domain Management Overview -->
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg class="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9"></path>
                  </svg>
                  Domain Management
                </h4>
                <p class="text-sm text-gray-600 mb-6">Configure your domain settings for this individual store. You can set up a subdomain or connect your own custom domain.</p>
                
                <!-- Domain Options Tabs -->
                <div class="mb-6">
                  <div class="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                    <button
                      @click="domainManagementTab = 'subdomain'"
                      :class="[
                        'flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors',
                        domainManagementTab === 'subdomain'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800'
                      ]"
                    >
                      Subdomain
                    </button>
                    <button
                      @click="domainManagementTab = 'custom-domain'"
                      :class="[
                        'flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors',
                        domainManagementTab === 'custom-domain'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800'
                      ]"
                    >
                      Custom Domain
                    </button>
                  </div>
                </div>

                <!-- Subdomain Section -->
                <div v-if="domainManagementTab === 'subdomain'" class="space-y-4">
                  <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h5 class="text-sm font-medium text-blue-800 mb-2">About Subdomains</h5>
                    <p class="text-xs text-blue-700 mb-3">A subdomain is a free, branded URL that you can customize for your landing page.</p>
                    <ul class="text-xs text-blue-700 space-y-1">
                      <li>• Your landing page will be accessible at: <strong>{{ getSubdomainUrl('your-subdomain', 'landing') }}</strong></li>
                      <li>• Subdomains can only contain letters, numbers, and hyphens</li>
                      <li>• Must be between 3-63 characters long</li>
                      <li>• Cannot start or end with a hyphen</li>
                      <li>• Changes may take a few minutes to propagate</li>
                    </ul>
                  </div>

                  <!-- Current Subdomain Display -->
                  <div v-if="currentSubdomain" class="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div class="flex items-center justify-between">
                      <div>
                        <h5 class="text-sm font-medium text-green-800">Current Subdomain</h5>
                        <p class="text-lg font-semibold text-green-900">
                          <a 
                            :href="`https://${getSubdomainUrl(currentSubdomain, 'landing')}`" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            class="text-green-900 hover:text-green-700 underline"
                          >
                            {{ getSubdomainUrl(currentSubdomain, 'landing') }}
                            <svg class="w-4 h-4 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                            </svg>
                          </a>
                        </p>
                        <p class="text-xs text-green-600 mt-1">Click the URL above to preview your landing page</p>
                      </div>
                      <div class="flex gap-2 justify-end">
                        <a 
                          :href="`https://${getSubdomainUrl(currentSubdomain, 'landing')}`" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                          style="color: white !important;"
                        >
                          Preview
                        </a>
                        <button
                          @click="openSubdomainDialog"
                          class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                        >
                          Edit Subdomain
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- No Subdomain State -->
                  <div v-else class="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div class="flex items-center justify-between">
                      <div>
                        <h5 class="text-sm font-medium text-yellow-800">No Subdomain Configured</h5>
                        <p class="text-sm text-yellow-700">Create a custom subdomain for this landing page</p>
                      </div>
                      <button
                        @click="openSubdomainDialog"
                        class="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors text-sm font-medium"
                      >
                        Create Subdomain
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Custom Domain Section -->
                <div v-if="domainManagementTab === 'custom-domain'" class="space-y-4">
                  <div class="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h5 class="text-sm font-medium text-purple-800 mb-2">About Custom Domains</h5>
                    <p class="text-xs text-purple-700 mb-3">Connect your own domain for a professional branded experience. You'll need to configure DNS settings with your domain provider.</p>
                    <ul class="text-xs text-purple-700 space-y-1">
                      <li>• Use your own domain (e.g., landing.yourdomain.com)</li>
                      <li>• Requires DNS configuration with your domain provider</li>
                      <li>• DNS changes can take up to 48 hours to propagate</li>
                      <li>• SSL certificates are automatically provisioned</li>
                    </ul>
                  </div>

                  <!-- Current Custom Domain Display -->
                  <div v-if="currentCustomDomain" class="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div class="flex items-center justify-between">
                      <div>
                        <h5 class="text-sm font-medium text-green-800">Current Custom Domain</h5>
                        <p class="text-lg font-semibold text-green-900">
                          <a 
                            :href="`https://${currentCustomDomain}`" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            class="text-green-900 hover:text-green-700 underline"
                          >
                            {{ currentCustomDomain }}
                            <svg class="w-4 h-4 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                            </svg>
                          </a>
                        </p>
                        <p class="text-xs text-green-600 mt-1">Your custom domain has been configured. Please ensure DNS records are properly set up.</p>
                      </div>
                      <div class="flex gap-2 justify-end">
                        <a 
                          :href="`https://${currentCustomDomain}`" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                          style="color: white !important;"
                        >
                          Preview
                        </a>
                        <button
                          @click="openCustomDomainDialog"
                          class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                        >
                          Edit Domain
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- No Custom Domain State -->
                  <div v-else class="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div class="flex items-center justify-between">
                      <div>
                        <h5 class="text-sm font-medium text-yellow-800">No Custom Domain Configured</h5>
                        <p class="text-sm text-yellow-700">Connect your own domain for a professional branded experience</p>
                      </div>
                      <button
                        @click="openCustomDomainDialog"
                        class="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors text-sm font-medium"
                      >
                        Connect Domain
                      </button>
                    </div>
                  </div>

                  <!-- Custom Domain Setup Instructions -->
                  <div class="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h5 class="text-sm font-medium text-purple-800 mb-3 flex items-center">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      How to Connect Your Custom Domain
                    </h5>
                    <div class="space-y-3">
                      <div class="bg-white rounded-lg p-3 border border-purple-100">
                        <h6 class="text-sm font-medium text-purple-900 mb-2">Step 1: Add Your Domain</h6>
                        <p class="text-xs text-purple-700">Enter your domain (e.g., <code class="bg-purple-100 px-1 rounded">landing.yourdomain.com</code>) in the domain field above.</p>
                      </div>
                      <div class="bg-white rounded-lg p-3 border border-purple-100">
                        <h6 class="text-sm font-medium text-purple-900 mb-2">Step 2: Configure DNS Records</h6>
                        <p class="text-xs text-purple-700 mb-2">In your domain's DNS settings, add a CNAME record (sample values shown below):</p>
                        <div class="bg-gray-50 rounded-lg overflow-hidden">
                          <table class="w-full text-xs font-mono">
                            <thead class="bg-gray-100">
                              <tr>
                                <th class="px-3 py-2 text-left text-gray-600 font-medium">Record Type</th>
                                <th class="px-3 py-2 text-left text-gray-600 font-medium">Name/Host</th>
                                <th class="px-3 py-2 text-left text-gray-600 font-medium">Value/Target</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr class="border-t border-gray-200">
                                <td class="px-3 py-2 text-gray-700">CNAME</td>
                                <td class="px-3 py-2 text-purple-900 font-semibold">site</td>
                                <td class="px-3 py-2 text-purple-900 font-semibold">{{ getCustomDomainCnameTarget(currentSubdomain || 'your-subdomain', 'landing') }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div class="bg-white rounded-lg p-3 border border-purple-100">
                        <h6 class="text-sm font-medium text-purple-900 mb-2">Step 3: Verify Connection</h6>
                        <p class="text-xs text-purple-700">Once DNS propagation is complete (up to 48 hours), your custom domain will be active.</p>
                      </div>
                    </div>
                  </div>

                  <!-- Important Notes -->
                  <div class="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h5 class="text-sm font-medium text-orange-800 mb-2 flex items-center">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                      </svg>
                      Important Notes
                    </h5>
                    <ul class="text-xs text-orange-700 space-y-1">
                      <li>• DNS changes can take up to 48 hours to propagate globally</li>
                      <li>• Ensure your domain provider supports CNAME records</li>
                      <li>• The CNAME record must point to your exact subdomain URL</li>
                      <li>• You can use any subdomain prefix (site, store, locator, etc.)</li>
                      <li>• SSL certificates are automatically provisioned for custom domains</li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Subdomain Edit Dialog -->
              <div v-if="showSubdomainEditDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div class="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
                  <h3 class="text-lg font-semibold text-gray-900 mb-4">
                    {{ currentSubdomain ? 'Edit Subdomain' : 'Create Subdomain' }}
                  </h3>
                  <div class="space-y-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">Subdomain Name</label>
                      <div class="flex items-center">
                        <input
                          ref="subdomainInput"
                          v-model="newSubdomain"
                          type="text"
                          placeholder="your-store-name"
                          class="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          :class="{ 'border-red-500': subdomainError }"
                        />
                        <span class="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-gray-600 text-sm">
                          .{{ getDomainConfig().subdomainSuffix }}
                        </span>
                      </div>
                      <p v-if="subdomainError" class="text-red-500 text-xs mt-1">{{ subdomainError }}</p>
                      <p v-else class="text-gray-500 text-xs mt-1">
                        Your landing page will be accessible at: <strong>{{ getSubdomainUrl(newSubdomain || 'your-subdomain', 'landing') }}</strong>
                      </p>
                    </div>
                    <div v-if="subdomainAvailability" class="p-3 rounded-lg" :class="{
                      'bg-green-50 border border-green-200 text-green-800': subdomainAvailability === 'available',
                      'bg-red-50 border border-red-200 text-red-800': subdomainAvailability === 'unavailable',
                      'bg-yellow-50 border border-yellow-200 text-yellow-800': subdomainAvailability === 'checking'
                    }">
                      <div class="flex items-center">
                        <svg v-if="subdomainAvailability === 'available'" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <svg v-else-if="subdomainAvailability === 'unavailable'" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                        <svg v-else class="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        <span class="text-sm font-medium">
                          {{ subdomainAvailability === 'available' ? 'Subdomain is available' : 
                             subdomainAvailability === 'unavailable' ? 'Subdomain is not available' : 
                             'Checking availability...' }}
                        </span>
                      </div>
                    </div>
                    <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <h5 class="text-sm font-medium text-blue-800 mb-2">Subdomain Requirements</h5>
                      <ul class="text-xs text-blue-700 space-y-1">
                        <li>• Only letters, numbers, and hyphens allowed</li>
                        <li>• Must be between 3-63 characters</li>
                        <li>• Cannot start or end with a hyphen</li>
                        <li>• Cannot contain consecutive hyphens</li>
                      </ul>
                    </div>
                  </div>
                  <div class="flex gap-3 justify-end mt-6">
                    <button
                      @click="closeSubdomainDialog"
                      :disabled="isSavingSubdomain"
                      class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      @click="saveSubdomain"
                      :disabled="!newSubdomain || subdomainError || subdomainAvailability !== 'available' || isSavingSubdomain"
                      class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      @mouseenter="debugButtonState"
                    >
                      <span v-if="isSavingSubdomain" class="flex items-center">
                        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                      <span v-else>{{ currentSubdomain ? 'Update Subdomain' : 'Create Subdomain' }}</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Custom Domain Edit Dialog -->
              <div v-if="showCustomDomainDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div class="bg-white rounded-lg p-8 max-w-2xl w-full mx-4">
                  <h3 class="text-xl font-semibold text-gray-900 mb-6">
                    {{ currentCustomDomain ? 'Edit Custom Domain' : 'Connect Custom Domain' }}
                  </h3>
                  
                  <div class="space-y-6">
                    <!-- Domain Input -->
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">Custom Domain</label>
                      <input
                        ref="customDomainInput"
                        v-model="newCustomDomain"
                        type="text"
                        placeholder="landing.yourdomain.com"
                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                        :class="{ 'border-red-500': customDomainError }"
                      />
                      <p v-if="customDomainError" class="text-red-500 text-sm mt-2">{{ customDomainError }}</p>
                      <p v-else class="text-gray-500 text-sm mt-2">
                        Enter your custom domain (e.g., landing.yourdomain.com)
                      </p>
                    </div>

                    <!-- Domain Validation Status -->
                    <div v-if="customDomainAvailability" class="p-4 rounded-lg" :class="{
                      'bg-green-50 border border-green-200 text-green-800': customDomainAvailability === 'available',
                      'bg-red-50 border border-red-200 text-red-800': customDomainAvailability === 'unavailable',
                      'bg-yellow-50 border border-yellow-200 text-yellow-800': customDomainAvailability === 'checking'
                    }">
                      <div class="flex items-center">
                        <svg v-if="customDomainAvailability === 'available'" class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <svg v-else-if="customDomainAvailability === 'unavailable'" class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                        <svg v-else class="w-5 h-5 mr-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        <span class="text-sm font-medium">
                          {{ customDomainAvailability === 'available' ? 'Domain format is valid' : 
                             customDomainAvailability === 'unavailable' ? 'Invalid domain format' : 
                             'Checking domain...' }}
                        </span>
                      </div>
                      <!-- DNS Verification Note -->
                      <div v-if="customDomainAvailability === 'available'" class="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div class="flex items-start">
                          <svg class="w-4 h-4 mr-2 mt-0.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                          <div class="text-xs text-blue-700">
                            <p class="font-medium mb-1">Next Steps</p>
                            <p>Domain format is valid. Please configure your DNS records according to the instructions below to complete the setup.</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- DNS Configuration Section -->
                    <div class="bg-gray-50 rounded-lg p-6">
                      <h4 class="text-lg font-semibold text-gray-900 mb-4">Configure your domain manually by adding this record</h4>
                      
                      <!-- DNS Records Table -->
                      <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <table class="w-full">
                          <thead class="bg-gray-50">
                            <tr>
                              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RECORD TYPE</th>
                              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">HOST NAME</th>
                              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">REQUIRED VALUE</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr class="border-t border-gray-200">
                              <td class="px-4 py-4 text-sm font-medium text-gray-900">CNAME</td>
                              <td class="px-4 py-4">
                                <div class="flex items-center">
                                  <span class="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-mono">{{ getDomainParts(newCustomDomain || 'landing.yourdomain.com')?.subdomain || 'landing' }}</span>
                                  <button
                                    @click="copyToClipboard(getDomainParts(newCustomDomain || 'landing.yourdomain.com')?.subdomain || 'landing')"
                                    class="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Copy to clipboard"
                                  >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                    </svg>
                                  </button>
                                </div>
                              </td>
                              <td class="px-4 py-4">
                                <div class="flex items-center">
                                  <span class="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-mono">{{ getCustomDomainCnameTarget(currentSubdomain || 'your-subdomain', 'landing') }}</span>
                                  <button
                                    @click="copyToClipboard(getCustomDomainCnameTarget(currentSubdomain || 'your-subdomain', 'landing'))"
                                    class="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Copy to clipboard"
                                  >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Forward Instructions -->
                      <div class="mt-3">
                        <button
                          @click="forwardInstructions"
                          class="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                        >
                          >> Forward instructions to a colleague
                          <svg class="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                          </svg>
                        </button>
                      </div>

                      <!-- Propagation Note -->
                      <div class="mt-4 text-gray-500 text-sm">
                        The changes may take up to 48 hours to reflect
                      </div>
                    </div>
                  </div>

                  <div class="flex gap-3 justify-end mt-8 pt-6 border-t border-gray-200">
                    <button
                      @click="closeCustomDomainDialog"
                      :disabled="isSavingCustomDomain"
                      class="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      @click="saveCustomDomain"
                      :disabled="!newCustomDomain || customDomainError || customDomainAvailability !== 'available' || isSavingCustomDomain"
                      class="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                    >
                      <span v-if="isSavingCustomDomain" class="flex items-center">
                        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                      <span v-else>{{ currentCustomDomain ? 'Update Domain' : 'Connect Domain' }}</span>
                    </button>
                  </div>
                </div>
                            </div>
            </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex gap-4 mt-8 pt-6 border-t border-gray-200">
            <button
              @click="handleSaveClick"
              class="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium"
            >
              Save Configuration
            </button>
            <button
              @click="resetToDefaults"
              class="px-6 py-3 bg-gray-500 text-white rounded-md hover:bg-gray-600 font-medium"
            >
              Reset to Defaults
            </button>
            <button
              @click="revertAllChanges"
              class="px-6 py-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 font-medium"
            >
              Revert All Changes
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Dialogs -->
    <!-- Save Confirmation Dialog -->
    <div v-if="showSaveDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Save Configuration</h3>
        <p class="text-gray-600 mb-6">Are you sure you want to save all the configuration changes? This will update your landing page settings.</p>
        <div class="flex gap-3 justify-end">
          <button
            @click="showSaveDialog = false"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            @click="confirmSave"
            class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>

    <!-- Back Confirmation Dialog -->
    <div v-if="showBackDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Unsaved Changes</h3>
        <p class="text-gray-600 mb-6">You have unsaved changes. Are you sure you want to go back? All unsaved changes will be lost.</p>
        <div class="flex gap-3 justify-end">
          <button
            @click="showBackDialog = false"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Stay Here
          </button>
          <button
            @click="confirmBack"
            class="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Product/Service Confirmation Dialog -->
    <div v-if="showDeleteProductDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Delete Product/Service</h3>
        <p class="text-gray-600 mb-6">
          Are you sure you want to delete "<strong>{{ productToDelete?.name || 'this product/service' }}</strong>"? 
          This action cannot be undone.
        </p>
        <div class="flex gap-3 justify-end">
          <button
            @click="showDeleteProductDialog = false"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            @click="confirmDeleteProduct"
            class="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import IndividualStore from './IndividualStore.vue';
import { db } from '@/utils/firebase.utils';
import { collection, getDocs, doc, getDoc, updateDoc, setDoc, deleteDoc, query, orderBy, limit, addDoc, where } from 'firebase/firestore';
import { getSubdomainUrl, getCustomDomainCnameTarget, getDomainConfig } from '@/utils/domainConfig';

export default {
  name: 'IndividualStoreConfigurations',
  components: { IndividualStore },
  data() {
    return {
      showSaveDialog: false,
      showBackDialog: false,
      showDeleteProductDialog: false,
      productToDelete: null,
      originalLocationStyle: null,
      originalCtaAppointments: null,
      originalHeaderStyle: null,
      originalHeroSection: null,
      originalReviewsSection: null,
      originalContactInformation: null,
      originalAppointmentStyles: null,
      originalProductStyles: null,
      originalPostsSection: null,
      originalBlogSection: null,
      originalGallerySection: null,
      originalFooterSection: null,
      originalSocialSection: null,
      originalSectionHeaders: null,
      originalProductsServices: null,
      selectedLocationId: '',
      selectedLocation: null,
      locations: [],
      activeTab: 'appointments-cta',
      storeRefreshKey: 0, // Key to force re-render of IndividualStore component
      isRefreshingPreview: false, // Loading state for refresh preview button
      isEnablingAllAppointments: false, // Loading state for bulk appointment enable
      isDisablingAllAppointments: false, // Loading state for bulk appointment disable
      // Theme management
      currentTheme: 'default',
      themeDraft: {},
      originalTheme: null,
      predefinedThemes: {
        default: {
          name: 'Default Blue',
          description: 'Clean blue theme with professional appearance',
          preview: '#3b82f6',
          variables: {
            // Header styles
            '--header-text-color': '#1f2937',
            '--header-background-color': '#ffffff',
            '--primary-title-color': '#1f2937',
            
            // Hero section
            '--hero-brand-name-color': '#1f2937',
            '--hero-description-color': '#6b7280',
            '--hero-section-bg-color': 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
            
            // Reviews section
            '--reviews-section-bg-color': '#f9fafb',
            '--review-card-bg-color': '#ffffff',
            '--review-card-text-color': '#1f2937',
            
            // Contact section
            '--contact-section-bg-color': '#ffffff',
            '--contact-card-bg-color': '#f9fafb',
            '--contact-card-border-color': '#e5e7eb',
            '--contact-card-border-radius': '0.75rem',
            '--contact-text-color': '#374151',
            '--contact-label-color': '#1f2937',
            '--contact-link-color': '#3b82f6',
            '--contact-description-color': '#6b7280',
            
            // Appointment styles
            '--appointment-button-bg-color': 'linear-gradient(to right, #3b82f6, #1d4ed8)',
            '--appointment-button-text-color': '#ffffff',
            '--appointment-button-hover-bg-color': 'linear-gradient(to right, #2563eb, #1e40af)',
            '--appointment-button-hover-text-color': '#ffffff',
            '--appointment-section-bg-color': '#f9fafb',
            '--appointment-title-color': '#1f2937',
            '--appointment-description-color': '#6b7280',
            '--appointment-form-bg-color': '#ffffff',
            '--appointment-form-title-color': '#1f2937',
            '--appointment-label-color': '#374151',
            
            // Product styles
            '--product-section-bg-color': '#ffffff',
            '--product-card-bg-color': '#ffffff',
            '--product-card-border-color': '#e5e7eb',
            
            // Posts section
            '--posts-section-bg-color': '#ffffff',
            '--post-card-bg-color': '#f9fafb',
            '--post-card-border-color': '#e5e7eb',
            '--post-title-color': '#1f2937',
            '--post-text-color': '#374151',
            '--post-meta-color': '#6b7280',
            
            // Blog section
            '--blog-section-bg-color': '#ffffff',
            '--blog-card-bg-color': '#f9fafb',
            '--blog-card-border-color': '#e5e7eb',
            '--blog-title-color': '#1f2937',
            '--blog-text-color': '#374151',
            '--blog-meta-color': '#6b7280',
            '--blog-excerpt-color': '#6b7280',
            '--blog-tag-color': '#3b82f6',
            
            // Gallery section
            '--gallery-section-bg-color': '#ffffff',
            '--gallery-card-bg-color': '#f9fafb',
            '--gallery-card-border-color': '#e5e7eb',
            '--gallery-card-border-radius': '0.5rem',
            '--gallery-card-shadow-color': 'rgba(0, 0, 0, 0.1)',
            '--gallery-title-color': '#1f2937',
            '--gallery-subtitle-color': '#6b7280',
            '--gallery-text-color': '#374151',
            '--gallery-image-border-radius': '0.5rem',
            '--gallery-image-shadow': '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            '--gallery-hover-shadow': '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            '--gallery-overlay-color': 'rgba(0, 0, 0, 0.5)',
            '--gallery-icon-color': '#ffffff',
            '--gallery-button-bg-color': '#3b82f6',
            '--gallery-button-text-color': '#ffffff',
            '--gallery-button-hover-bg-color': '#2563eb',
            '--gallery-button-hover-text-color': '#ffffff',
            
            // Footer section
            '--footer-bg-color': '#ffffff',
            '--footer-border-color': '#e5e7eb',
            '--footer-shadow-color': 'rgba(0, 0, 0, 0.1)',
            '--footer-brand-name-color': '#1f2937',
            '--footer-text-color': '#6b7280',
            '--footer-copyright-color': '#6b7280',

            
            // Social section
            '--social-section-bg-color': '#f9fafb',
            '--social-title-color': '#1f2937',
            '--social-subtitle-color': '#6b7280',
            '--social-icon-color': '#3b82f6',
            '--social-icon-hover-color': '#1d4ed8',
            
            // Social links styles
            '--social-links-icon-size': '24px',
            '--social-links-icon-color': '#6b7280',
            '--social-links-icon-hover-color': '#3b82f6',
            '--social-links-icon-bg-color': 'transparent',
            '--social-links-icon-border-radius': '0.5rem',
            '--social-links-icon-spacing': '1rem',
            '--social-links-container-padding': '1rem'
          }
        },
        modern: {
          name: 'Modern Dark',
          description: 'Sleek dark theme with pure black header/footer and lighter sections',
          preview: '#000000',
          variables: {
            // Header styles - Pure black
            '--header-text-color': '#ffffff',
            '--header-background-color': '#000000',
            '--primary-title-color': '#ffffff',
            
            // Hero section - Lighter shade
            '--hero-brand-name-color': '#ffffff',
            '--hero-description-color': '#ffffff',
            '--hero-section-bg-color': 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            
            // Reviews section - Lighter shade
            '--reviews-section-bg-color': '#1a1a1a',
            '--review-card-bg-color': '#2d2d2d',
            '--review-card-text-color': '#ffffff',
            
            // Contact section - Lighter shade
            '--contact-section-bg-color': '#1a1a1a',
            '--contact-card-bg-color': '#2d2d2d',
            '--contact-card-border-color': '#404040',
            '--contact-card-border-radius': '0.75rem',
            '--contact-text-color': '#ffffff',
            '--contact-label-color': '#ffffff',
            '--contact-link-color': '#10b981',
            '--contact-description-color': '#cccccc',
            
            // Appointment styles - Dark theme
            '--appointment-button-bg-color': 'linear-gradient(to right, #374151, #1f2937)',
            '--appointment-button-text-color': '#ffffff',
            '--appointment-button-hover-bg-color': 'linear-gradient(to right, #1f2937, #111827)',
            '--appointment-button-hover-text-color': '#ffffff',
            '--appointment-section-bg-color': '#1a1a1a',
            '--appointment-title-color': '#ffffff',
            '--appointment-description-color': '#cccccc',
            '--appointment-form-bg-color': '#2d2d2d',
            '--appointment-form-title-color': '#ffffff',
            '--appointment-label-color': '#cccccc',
            '--appointment-button-border-radius': '0.5rem',
            '--appointment-button-border-size': '0px',
            '--appointment-button-border-color': 'transparent',
            
            // Product styles - Lighter shade
            '--product-section-bg-color': '#1a1a1a',
            '--product-card-bg-color': '#2d2d2d',
            '--product-card-border-color': '#404040',
            '--product-card-border-radius': '1rem',
            '--product-card-border-size': '1px',
            '--product-image-border-radius': '0.75rem',
            
            // Posts section - Lighter shade (like posts cards)
            '--posts-section-bg-color': '#1a1a1a',
            '--post-card-bg-color': '#2d2d2d',
            '--post-card-border-color': '#404040',
            '--post-card-border-radius': '0.5rem',
            '--post-title-color': '#ffffff',
            '--post-text-color': '#ffffff',
            '--post-meta-color': '#cccccc',
            
            // Blog section - Lighter shade (like posts cards)
            '--blog-section-bg-color': '#1a1a1a',
            '--blog-card-bg-color': '#2d2d2d',
            '--blog-card-border-color': '#404040',
            '--blog-card-border-radius': '0.5rem',
            '--blog-title-color': '#ffffff',
            '--blog-text-color': '#ffffff',
            '--blog-meta-color': '#cccccc',
            '--blog-excerpt-color': '#cccccc',
            '--blog-tag-color': '#10b981',
            
            // Gallery section - Dark theme
            '--gallery-section-bg-color': '#000000',
            '--gallery-card-bg-color': '#2d2d2d',
            '--gallery-card-border-color': '#404040',
            '--gallery-card-border-radius': '0.5rem',
            '--gallery-card-shadow-color': 'rgba(0, 0, 0, 0.3)',
            '--gallery-title-color': '#ffffff',
            '--gallery-subtitle-color': '#cccccc',
            '--gallery-text-color': '#ffffff',
            '--gallery-image-border-radius': '0.5rem',
            '--gallery-image-shadow': '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '--gallery-hover-shadow': '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
            '--gallery-overlay-color': 'rgba(0, 0, 0, 0.7)',
            '--gallery-icon-color': '#ffffff',
            '--gallery-button-bg-color': '#10b981',
            '--gallery-button-text-color': '#ffffff',
            '--gallery-button-hover-bg-color': '#059669',
            '--gallery-button-hover-text-color': '#ffffff',
            
            // Footer section - Pure black
            '--footer-bg-color': '#000000',
            '--footer-border-color': '#404040',
            '--footer-shadow-color': 'rgba(0, 0, 0, 0.5)',
            '--footer-brand-name-color': '#ffffff',
            '--footer-text-color': '#ffffff',
            '--footer-copyright-color': '#cccccc',
            
            // Social section - Lighter shade
            '--social-section-bg-color': '#1a1a1a',
            '--social-title-color': '#ffffff',
            '--social-subtitle-color': '#ffffff',
            '--social-icon-color': '#10b981',
            '--social-icon-hover-color': '#059669',
            
            // Social links styles
            '--social-links-icon-size': '24px',
            '--social-links-icon-color': '#cccccc',
            '--social-links-icon-hover-color': '#10b981',
            '--social-links-icon-bg-color': 'transparent',
            '--social-links-icon-border-radius': '0.5rem',
            '--social-links-icon-spacing': '1rem',
            '--social-links-container-padding': '1rem'
          }
        },
        warm: {
          name: 'Warm Orange',
          description: 'Warm and inviting orange theme',
          preview: '#f97316',
          variables: {
            // Header styles
            '--header-text-color': '#1f2937',
            '--header-background-color': '#ffffff',
            '--primary-title-color': '#9a3412',
            
            // Hero section
            '--hero-brand-name-color': '#9a3412',
            '--hero-description-color': '#6b7280',
            '--hero-section-bg-color': 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
            
            // Reviews section
            '--reviews-section-bg-color': '#fff7ed',
            '--review-card-bg-color': '#ffffff',
            '--review-card-text-color': '#1f2937',
            
            // Contact section
            '--contact-section-bg-color': '#ffffff',
            '--contact-card-bg-color': '#fff7ed',
            '--contact-card-border-color': '#fed7aa',
            '--contact-card-border-radius': '0.75rem',
            
            // Appointment styles
            '--appointment-button-bg-color': 'linear-gradient(to right, #f97316, #ea580c)',
            '--appointment-button-text-color': '#ffffff',
            '--appointment-button-hover-bg-color': 'linear-gradient(to right, #ea580c, #c2410c)',
            '--appointment-button-hover-text-color': '#ffffff',
            '--appointment-section-bg-color': '#fff7ed',
            '--appointment-title-color': '#9a3412',
            '--appointment-description-color': '#6b7280',
            '--appointment-form-bg-color': '#ffffff',
            '--appointment-form-title-color': '#9a3412',
            '--appointment-label-color': '#374151',
            
            // Product styles
            '--product-section-bg-color': '#ffffff',
            '--product-card-bg-color': '#ffffff',
            '--product-card-border-color': '#fed7aa',
            
            // Posts section
            '--posts-section-bg-color': '#ffffff',
            '--post-card-bg-color': '#fff7ed',
            '--post-card-border-color': '#fed7aa',
            '--post-title-color': '#9a3412',
            '--post-text-color': '#374151',
            '--post-meta-color': '#f97316',
            
            // Blog section
            '--blog-section-bg-color': '#ffffff',
            '--blog-card-bg-color': '#fff7ed',
            '--blog-card-border-color': '#fed7aa',
            '--blog-title-color': '#9a3412',
            '--blog-text-color': '#374151',
            '--blog-meta-color': '#f97316',
            '--blog-excerpt-color': '#f97316',
            '--blog-tag-color': '#f97316',
            
            // Gallery section - Warm theme
            '--gallery-section-bg-color': '#ffffff',
            '--gallery-card-bg-color': '#fff7ed',
            '--gallery-card-border-color': '#fed7aa',
            '--gallery-card-border-radius': '0.5rem',
            '--gallery-card-shadow-color': 'rgba(0, 0, 0, 0.1)',
            '--gallery-title-color': '#9a3412',
            '--gallery-subtitle-color': '#f97316',
            '--gallery-text-color': '#374151',
            '--gallery-image-border-radius': '0.5rem',
            '--gallery-image-shadow': '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            '--gallery-hover-shadow': '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            '--gallery-overlay-color': 'rgba(0, 0, 0, 0.5)',
            '--gallery-icon-color': '#ffffff',
            '--gallery-button-bg-color': '#f97316',
            '--gallery-button-text-color': '#ffffff',
            '--gallery-button-hover-bg-color': '#ea580c',
            '--gallery-button-hover-text-color': '#ffffff',
            
            // Footer section
            '--footer-bg-color': '#ffffff',
            '--footer-border-color': '#fed7aa',
            '--footer-shadow-color': 'rgba(0, 0, 0, 0.1)',
            '--footer-brand-name-color': '#9a3412',
            '--footer-text-color': '#f97316',
            '--footer-copyright-color': '#f97316',
            
            // Social section
            '--social-section-bg-color': '#fff7ed',
            '--social-title-color': '#9a3412',
            '--social-subtitle-color': '#f97316',
            '--social-icon-color': '#f97316',
            '--social-icon-hover-color': '#ea580c',
            
            // Social links styles
            '--social-links-icon-size': '24px',
            '--social-links-icon-color': '#f97316',
            '--social-links-icon-hover-color': '#ea580c',
            '--social-links-icon-bg-color': 'transparent',
            '--social-links-icon-border-radius': '0.5rem',
            '--social-links-icon-spacing': '1rem',
            '--social-links-container-padding': '1rem'
          }
        },
        elegant: {
          name: 'Elegant Purple',
          description: 'Sophisticated purple theme for premium brands',
          preview: '#8b5cf6',
          variables: {
            // Header styles
            '--header-text-color': '#1f2937',
            '--header-background-color': '#ffffff',
            '--primary-title-color': '#581c87',
            
            // Hero section
            '--hero-brand-name-color': '#581c87',
            '--hero-description-color': '#6b7280',
            '--hero-section-bg-color': 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
            
            // Reviews section
            '--reviews-section-bg-color': '#faf5ff',
            '--review-card-bg-color': '#ffffff',
            '--review-card-text-color': '#1f2937',
            
            // Contact section
            '--contact-section-bg-color': '#ffffff',
            '--contact-card-bg-color': '#faf5ff',
            '--contact-card-border-color': '#e9d5ff',
            '--contact-card-border-radius': '0.75rem',
            
            // Appointment styles
            '--appointment-button-bg-color': 'linear-gradient(to right, #8b5cf6, #7c3aed)',
            '--appointment-button-text-color': '#ffffff',
            '--appointment-button-hover-bg-color': 'linear-gradient(to right, #7c3aed, #6d28d9)',
            '--appointment-button-hover-text-color': '#ffffff',
            '--appointment-section-bg-color': '#faf5ff',
            '--appointment-title-color': '#581c87',
            '--appointment-description-color': '#6b7280',
            '--appointment-form-bg-color': '#ffffff',
            '--appointment-form-title-color': '#581c87',
            '--appointment-label-color': '#374151',
            
            // Product styles
            '--product-section-bg-color': '#ffffff',
            '--product-card-bg-color': '#ffffff',
            '--product-card-border-color': '#e9d5ff',
            
            // Posts section
            '--posts-section-bg-color': '#ffffff',
            '--post-card-bg-color': '#faf5ff',
            '--post-card-border-color': '#e9d5ff',
            '--post-title-color': '#581c87',
            '--post-text-color': '#374151',
            '--post-meta-color': '#8b5cf6',
            
            // Blog section
            '--blog-section-bg-color': '#ffffff',
            '--blog-card-bg-color': '#faf5ff',
            '--blog-card-border-color': '#e9d5ff',
            '--blog-title-color': '#581c87',
            '--blog-text-color': '#374151',
            '--blog-meta-color': '#8b5cf6',
            '--blog-excerpt-color': '#8b5cf6',
            '--blog-tag-color': '#8b5cf6',
            
            // Footer section
            '--footer-bg-color': '#ffffff',
            '--footer-border-color': '#e9d5ff',
            '--footer-shadow-color': 'rgba(0, 0, 0, 0.1)',
            '--footer-brand-name-color': '#581c87',
            '--footer-text-color': '#8b5cf6',
            '--footer-copyright-color': '#8b5cf6',
            
            // Social section
            '--social-section-bg-color': '#faf5ff',
            '--social-title-color': '#581c87',
            '--social-subtitle-color': '#8b5cf6',
            '--social-icon-color': '#8b5cf6',
            '--social-icon-hover-color': '#7c3aed',
            
            // Social links styles
            '--social-links-icon-size': '24px',
            '--social-links-icon-color': '#8b5cf6',
            '--social-links-icon-hover-color': '#7c3aed',
            '--social-links-icon-bg-color': 'transparent',
            '--social-links-icon-border-radius': '0.5rem',
            '--social-links-icon-spacing': '1rem',
            '--social-links-container-padding': '1rem'
          }
        },
        minimal: {
          name: 'Minimal Gray',
          description: 'Clean minimal theme with subtle grays',
          preview: '#6b7280',
          variables: {
            // Header styles
            '--header-text-color': '#1f2937',
            '--header-background-color': '#ffffff',
            '--primary-title-color': '#374151',
            
            // Hero section
            '--hero-brand-name-color': '#374151',
            '--hero-description-color': '#6b7280',
            '--hero-section-bg-color': 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
            
            // Reviews section
            '--reviews-section-bg-color': '#f9fafb',
            '--review-card-bg-color': '#ffffff',
            '--review-card-text-color': '#1f2937',
            
            // Contact section
            '--contact-section-bg-color': '#ffffff',
            '--contact-card-bg-color': '#f9fafb',
            '--contact-card-border-color': '#e5e7eb',
            '--contact-card-border-radius': '0.75rem',
            
            // Appointment styles
            '--appointment-button-bg-color': 'linear-gradient(to right, #6b7280, #4b5563)',
            '--appointment-button-text-color': '#ffffff',
            '--appointment-button-hover-bg-color': 'linear-gradient(to right, #4b5563, #374151)',
            '--appointment-button-hover-text-color': '#ffffff',
            '--appointment-section-bg-color': '#f9fafb',
            '--appointment-title-color': '#374151',
            '--appointment-description-color': '#6b7280',
            '--appointment-form-bg-color': '#ffffff',
            '--appointment-form-title-color': '#374151',
            '--appointment-label-color': '#374151',
            
            // Product styles
            '--product-section-bg-color': '#ffffff',
            '--product-card-bg-color': '#ffffff',
            '--product-card-border-color': '#e5e7eb',
            
            // Posts section
            '--posts-section-bg-color': '#ffffff',
            '--post-card-bg-color': '#f9fafb',
            '--post-card-border-color': '#e5e7eb',
            '--post-title-color': '#374151',
            '--post-text-color': '#6b7280',
            '--post-meta-color': '#9ca3af',
            
            // Blog section
            '--blog-section-bg-color': '#ffffff',
            '--blog-card-bg-color': '#f9fafb',
            '--blog-card-border-color': '#e5e7eb',
            '--blog-title-color': '#374151',
            '--blog-text-color': '#6b7280',
            '--blog-meta-color': '#9ca3af',
            '--blog-excerpt-color': '#9ca3af',
            '--blog-tag-color': '#6b7280',
            
            // Footer section
            '--footer-bg-color': '#ffffff',
            '--footer-border-color': '#e5e7eb',
            '--footer-shadow-color': 'rgba(0, 0, 0, 0.1)',
            '--footer-brand-name-color': '#374151',
            '--footer-text-color': '#6b7280',
            '--footer-copyright-color': '#9ca3af',
            
            // Social section
            '--social-section-bg-color': '#f9fafb',
            '--social-title-color': '#374151',
            '--social-subtitle-color': '#6b7280',
            '--social-icon-color': '#6b7280',
            '--social-icon-hover-color': '#4b5563',
            
            // Social links styles
            '--social-links-icon-size': '24px',
            '--social-links-icon-color': '#9ca3af',
            '--social-links-icon-hover-color': '#6b7280',
            '--social-links-icon-bg-color': 'transparent',
            '--social-links-icon-border-radius': '0.5rem',
            '--social-links-icon-spacing': '1rem',
            '--social-links-container-padding': '1rem'
          }
        },
        vibrant: {
          name: 'Vibrant Green',
          description: 'Energetic green theme for dynamic brands',
          preview: '#10b981',
          variables: {
            // Header styles
            '--header-text-color': '#1f2937',
            '--header-background-color': '#ffffff',
            '--primary-title-color': '#065f46',
            
            // Hero section
            '--hero-brand-name-color': '#065f46',
            '--hero-description-color': '#6b7280',
            '--hero-section-bg-color': 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
            
            // Reviews section
            '--reviews-section-bg-color': '#ecfdf5',
            '--review-card-bg-color': '#ffffff',
            '--review-card-text-color': '#1f2937',
            
            // Contact section
            '--contact-section-bg-color': '#ffffff',
            '--contact-card-bg-color': '#ecfdf5',
            '--contact-card-border-color': '#d1fae5',
            '--contact-card-border-radius': '0.75rem',
            
            // Appointment styles
            '--appointment-button-bg-color': 'linear-gradient(to right, #10b981, #059669)',
            '--appointment-button-text-color': '#ffffff',
            '--appointment-button-hover-bg-color': 'linear-gradient(to right, #059669, #047857)',
            '--appointment-button-hover-text-color': '#ffffff',
            '--appointment-section-bg-color': '#ecfdf5',
            '--appointment-title-color': '#065f46',
            '--appointment-description-color': '#6b7280',
            '--appointment-form-bg-color': '#ffffff',
            '--appointment-form-title-color': '#065f46',
            '--appointment-label-color': '#374151',
            
            // Product styles
            '--product-section-bg-color': '#ffffff',
            '--product-card-bg-color': '#ffffff',
            '--product-card-border-color': '#d1fae5',
            
            // Posts section
            '--posts-section-bg-color': '#ffffff',
            '--post-card-bg-color': '#ecfdf5',
            '--post-card-border-color': '#d1fae5',
            '--post-title-color': '#065f46',
            '--post-text-color': '#374151',
            '--post-meta-color': '#10b981',
            
            // Blog section
            '--blog-section-bg-color': '#ffffff',
            '--blog-card-bg-color': '#ecfdf5',
            '--blog-card-border-color': '#d1fae5',
            '--blog-title-color': '#065f46',
            '--blog-text-color': '#374151',
            '--blog-meta-color': '#10b981',
            '--blog-excerpt-color': '#10b981',
            '--blog-tag-color': '#10b981',
            
            // Gallery section - Vibrant theme
            '--gallery-section-bg-color': '#ffffff',
            '--gallery-card-bg-color': '#ecfdf5',
            '--gallery-card-border-color': '#d1fae5',
            '--gallery-card-border-radius': '0.5rem',
            '--gallery-card-shadow-color': 'rgba(0, 0, 0, 0.1)',
            '--gallery-title-color': '#065f46',
            '--gallery-subtitle-color': '#10b981',
            '--gallery-text-color': '#374151',
            '--gallery-image-border-radius': '0.5rem',
            '--gallery-image-shadow': '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            '--gallery-hover-shadow': '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            '--gallery-overlay-color': 'rgba(0, 0, 0, 0.5)',
            '--gallery-icon-color': '#ffffff',
            '--gallery-button-bg-color': '#10b981',
            '--gallery-button-text-color': '#ffffff',
            '--gallery-button-hover-bg-color': '#059669',
            '--gallery-button-hover-text-color': '#ffffff',
            
            // Footer section
            '--footer-bg-color': '#ffffff',
            '--footer-border-color': '#d1fae5',
            '--footer-shadow-color': 'rgba(0, 0, 0, 0.1)',
            '--footer-brand-name-color': '#065f46',
            '--footer-text-color': '#10b981',
            '--footer-copyright-color': '#10b981',
            
            // Social section
            '--social-section-bg-color': '#ecfdf5',
            '--social-title-color': '#065f46',
            '--social-subtitle-color': '#10b981',
            '--social-icon-color': '#10b981',
            '--social-icon-hover-color': '#059669',
            
            // Social links styles
            '--social-links-icon-size': '24px',
            '--social-links-icon-color': '#10b981',
            '--social-links-icon-hover-color': '#059669',
            '--social-links-icon-bg-color': 'transparent',
            '--social-links-icon-border-radius': '0.5rem',
            '--social-links-icon-spacing': '1rem',
            '--social-links-container-padding': '1rem'
          }
        }
      },
      configurationTabs: [
        { id: 'appointments-cta', label: 'Appointments & CTA' },
        { id: 'products-services', label: 'Products & Services' },
        { id: 'domain-management', label: 'Domain Management' },
        { id: 'themes', label: 'Themes' },
        { id: 'section-headers', label: 'Section Headers' },
        { id: 'header', label: 'Header Styles' },
        { id: 'hero', label: 'Hero Section' },
        { id: 'reviews', label: 'Reviews Section' },
        { id: 'contact', label: 'Contact Information' },
        { id: 'appointments', label: 'Appointment Styles' },
        { id: 'product-styles', label: 'Product Styles' },
        { id: 'posts', label: 'Posts Section' },
        { id: 'blogs', label: 'Blog Section' },
        { id: 'gallery', label: 'Gallery Section' },
        { id: 'footer', label: 'Footer Section' }
      ],
      // Tab scroll functionality
      canScrollLeft: false,
      canScrollRight: false,
      locationStyleDraft: {},
      ctaAppointmentsDraft: {},
      headerStyleDraft: {},
      heroSectionDraft: {},
      reviewsSectionDraft: {},
      contactInformationDraft: {},
      appointmentStylesDraft: {},
      productStylesDraft: {},
      postsSectionDraft: {},
      blogSectionDraft: {},
      gallerySectionDraft: {},
      footerSectionDraft: {},
      socialSectionDraft: {},
      sectionHeadersDraft: {},
      productsServicesDraft: [],
      csvImportStatus: null,
      draggedIndex: null,
      
      // Cloudinary Upload Properties
      cloudinaryWidget: null,
      currentUploadIndex: null,
      currentUploadType: null,
      isUploadingImage: false,
      isUploadingVideo: false,
      locationStyleFields: {
        // Features
        appointments: { label: 'Enable Appointment Booking', type: 'boolean', description: 'Show appointment booking section and button for this store', placeholder: false }
      },
      headerStyleFields: {
        // Logo and Title
        logoSize: { label: 'Logo Size', type: 'string', description: 'Size of the logo in header', placeholder: '3rem' },
        titleTextSize: { label: 'Title Text Size', type: 'string', description: 'Size of the title text', placeholder: '1.25rem' },
        
        // Colors
        textColor: { label: 'Text Color', type: 'color', description: 'Header text color', placeholder: '#1f2937' },
        backgroundColor: { label: 'Background Color', type: 'color', description: 'Header background color', placeholder: '#ffffff' },
        primaryTitleColor: { label: 'Primary Title Color', type: 'color', description: 'Color for all section titles', placeholder: '#1f2937' },
        
        // Tab Configuration
        tabName: { label: 'Tab Name', type: 'string', description: 'Browser tab name', placeholder: 'Store' },
        tabValue: { label: 'Tab Value', type: 'string', description: 'Dynamic value for tabs', placeholder: 'custom_value' },
        
        // Tab Styling
        tabTextColor: { label: 'Tab Text Color', type: 'color', description: 'Default tab text color', placeholder: '#333' },
        tabHoverTextColor: { label: 'Tab Hover Text Color', type: 'color', description: 'Tab text color on hover', placeholder: '#1773EA' },
        tabSelectedTextColor: { label: 'Tab Selected Text Color', type: 'color', description: 'Selected tab text color', placeholder: '#fff' },
        tabBg: { label: 'Tab Background', type: 'color', description: 'Default tab background', placeholder: '#f3f4f6' },
        tabHoverBg: { label: 'Tab Hover Background', type: 'color', description: 'Tab background on hover', placeholder: '#e0e7ff' },
        tabSelectedBg: { label: 'Tab Selected Background', type: 'color', description: 'Selected tab background', placeholder: '#1773EA' },
        
        // Tab Border Styling
        tabBorderColor: { label: 'Tab Border Color', type: 'color', description: 'Default tab border color', placeholder: '#e5e7eb' },
        tabHoverBorderColor: { label: 'Tab Hover Border Color', type: 'color', description: 'Tab border color on hover', placeholder: '#1773EA' },
        tabSelectedBorderColor: { label: 'Tab Selected Border Color', type: 'color', description: 'Selected tab border color', placeholder: '#1773EA' },
        tabBorderSize: { label: 'Tab Border Size', type: 'string', description: 'Default tab border size', placeholder: '1px' },
        tabHoverBorderSize: { label: 'Tab Hover Border Size', type: 'string', description: 'Tab border size on hover', placeholder: '2px' },
        tabSelectedBorderSize: { label: 'Tab Selected Border Size', type: 'string', description: 'Selected tab border size', placeholder: '2px' },
        tabBorderRadius: { label: 'Tab Border Radius', type: 'string', description: 'Tab border radius', placeholder: '0.5rem' }
      },
      ctaAppointmentsFields: {
        // Appointment Configuration
        appointments: { label: 'Enable Appointment Booking', type: 'boolean', description: 'Show appointment booking section and button for this store', placeholder: true },
        
        // CTA Configuration
        showAppointmentButton: { label: 'Show Appointment Button in Header', type: 'boolean', description: 'Display the "Book An Appointment" button in header', placeholder: true },
        showCallButton: { label: 'Show Call Button in Header', type: 'boolean', description: 'Display the "Call" button in header', placeholder: true },
        appointmentButtonText: { label: 'Appointment Button Text', type: 'string', description: 'Custom text for the appointment button', placeholder: 'Book An Appointment' },
        callButtonText: { label: 'Call Button Text', type: 'string', description: 'Custom text for the call button (use {{phone}} for phone number)', placeholder: 'Call {{phone}}' }
      },
      heroSectionFields: {
        // Brand Name Styling
        brandNameSize: { label: 'Brand Name Size', type: 'string', description: 'Size of the brand name in hero section', placeholder: '5rem' },
        brandNameColor: { label: 'Brand Name Color', type: 'color', description: 'Color of the brand name', placeholder: '#0f172a' },
        
        // Description Styling
        descriptionSize: { label: 'Description Size', type: 'string', description: 'Size of the description text', placeholder: '1.5rem' },
        descriptionColor: { label: 'Description Color', type: 'color', description: 'Color of the description text', placeholder: '#374151' },
        
        // Cover Photo Styling
        coverPhotoHeight: { label: 'Cover Photo Height', type: 'string', description: 'Height of the cover photo', placeholder: '28rem' },
        coverPhotoBorderRadius: { label: 'Cover Photo Border Radius', type: 'string', description: 'Border radius of the cover photo', placeholder: '1.5rem' },
        
        // Background
        backgroundColor: { label: 'Hero Background Color', type: 'color', description: 'Background color of the hero section', placeholder: '#f9fafb' }
      },
      reviewsSectionFields: {
        backgroundColor: { label: 'Reviews Section Background', type: 'color', description: 'Background color of the reviews section', placeholder: '#f9fafb' },
        cardBackgroundColor: { label: 'Review Card Background', type: 'color', description: 'Background color of review cards', placeholder: '#ffffff' },
        cardBorderRadius: { label: 'Review Card Border Radius', type: 'string', description: 'Border radius of review cards', placeholder: '1.5rem' },
        cardBorderSize: { label: 'Review Card Border Size', type: 'string', description: 'Border size of review cards', placeholder: '2px' },
        cardTextColor: { label: 'Review Card Text Color', type: 'color', description: 'Text color in review cards', placeholder: '#1f2937' }
      },
      contactInformationFields: {
        backgroundColor: { label: 'Contact Section Background', type: 'color', description: 'Background color of the contact section', placeholder: '#f8f9fa' },
        cardBackgroundColor: { label: 'Contact Card Background', type: 'color', description: 'Background color of contact cards', placeholder: '#f9fafb' },
        cardBorderColor: { label: 'Contact Card Border Color', type: 'color', description: 'Border color of contact cards', placeholder: '#e5e7eb' },
        cardBorderRadius: { label: 'Contact Card Border Radius', type: 'string', description: 'Border radius of contact cards', placeholder: '0.75rem' },
        textColor: { label: 'Contact Text Color', type: 'color', description: 'Color of contact section text', placeholder: '#374151' },
        labelColor: { label: 'Contact Label Color', type: 'color', description: 'Color of contact field labels (Phone, Email, etc.)', placeholder: '#1f2937' },
        linkColor: { label: 'Contact Link Color', type: 'color', description: 'Color of contact links (phone, email, website)', placeholder: '#3b82f6' },
        descriptionColor: { label: 'Contact Description Color', type: 'color', description: 'Color of contact section description text', placeholder: '#6b7280' }
      },
      appointmentStylesFields: {
        buttonBackgroundColor: { label: 'Button Background Color', type: 'string', description: 'Background color for appointment buttons', placeholder: 'linear-gradient(to right, #3b82f6, #1d4ed8)' },
        buttonTextColor: { label: 'Button Text Color', type: 'color', description: 'Text color for appointment buttons', placeholder: '#ffffff' },
        buttonBorderRadius: { label: 'Button Border Radius', type: 'string', description: 'Border radius for appointment buttons', placeholder: '0.5rem' },
        buttonBorderSize: { label: 'Button Border Size', type: 'string', description: 'Border size for appointment buttons', placeholder: '0px' },
        buttonBorderColor: { label: 'Button Border Color', type: 'color', description: 'Border color for appointment buttons', placeholder: 'transparent' },
        buttonHoverBackgroundColor: { label: 'Button Hover Background', type: 'string', description: 'Background color on button hover', placeholder: 'linear-gradient(to right, #2563eb, #1e40af)' },
        buttonHoverTextColor: { label: 'Button Hover Text Color', type: 'color', description: 'Text color on button hover', placeholder: '#ffffff' },
        sectionBackgroundColor: { label: 'Section Background Color', type: 'color', description: 'Background color of the appointment section', placeholder: '#f9fafb' },
        titleColor: { label: 'Title Color', type: 'color', description: 'Color of the appointment section title', placeholder: '#1f2937' },
        descriptionColor: { label: 'Description Color', type: 'color', description: 'Color of the appointment section description', placeholder: '#6b7280' },
        formBackgroundColor: { label: 'Form Background Color', type: 'color', description: 'Background color of the appointment form', placeholder: '#ffffff' },
        formTitleColor: { label: 'Form Title Color', type: 'color', description: 'Color of the form title', placeholder: '#1f2937' },
        labelColor: { label: 'Label Color', type: 'color', description: 'Color of form labels', placeholder: '#374151' }
      },
      productStylesFields: {
        sectionBackgroundColor: { label: 'Section Background Color', type: 'color', description: 'Background color of the products section', placeholder: '#ffffff' },
        cardBackgroundColor: { label: 'Card Background Color', type: 'color', description: 'Background color of product cards', placeholder: '#ffffff' },
        cardBorderRadius: { label: 'Card Border Radius', type: 'string', description: 'Border radius of product cards', placeholder: '1rem' },
        cardBorderSize: { label: 'Card Border Size', type: 'string', description: 'Border size of product cards', placeholder: '1px' },
        cardBorderColor: { label: 'Card Border Color', type: 'color', description: 'Border color of product cards', placeholder: '#e5e7eb' },
        imageBorderRadius: { label: 'Image Border Radius', type: 'string', description: 'Border radius of product images', placeholder: '0.75rem' }
      },
      postsSectionFields: {
        backgroundColor: { label: 'Section Background Color', type: 'color', description: 'Background color of the posts section', placeholder: '#ffffff' },
        cardBackgroundColor: { label: 'Card Background Color', type: 'color', description: 'Background color of post cards', placeholder: '#f9fafb' },
        cardBorderColor: { label: 'Card Border Color', type: 'color', description: 'Border color of post cards', placeholder: '#e5e7eb' },
        cardBorderRadius: { label: 'Card Border Radius', type: 'string', description: 'Border radius of post cards', placeholder: '0.5rem' },
        titleColor: { label: 'Title Color', type: 'color', description: 'Color of post titles', placeholder: '#1f2937' },
        textColor: { label: 'Text Color', type: 'color', description: 'Color of post text', placeholder: '#374151' },
        metaColor: { label: 'Meta Color', type: 'color', description: 'Color of post metadata (dates)', placeholder: '#6b7280' }
      },
      blogSectionFields: {
        backgroundColor: { label: 'Section Background Color', type: 'color', description: 'Background color of the blog section', placeholder: '#ffffff' },
        cardBackgroundColor: { label: 'Card Background Color', type: 'color', description: 'Background color of blog cards', placeholder: '#f9fafb' },
        cardBorderColor: { label: 'Card Border Color', type: 'color', description: 'Border color of blog cards', placeholder: '#e5e7eb' },
        cardBorderRadius: { label: 'Card Border Radius', type: 'string', description: 'Border radius of blog cards', placeholder: '0.5rem' },
        titleColor: { label: 'Title Color', type: 'color', description: 'Color of blog titles', placeholder: '#1f2937' },
        textColor: { label: 'Text Color', type: 'color', description: 'Color of blog text', placeholder: '#374151' },
        metaColor: { label: 'Meta Color', type: 'color', description: 'Color of blog metadata (dates)', placeholder: '#6b7280' },
        excerptColor: { label: 'Excerpt Color', type: 'color', description: 'Color of blog excerpts', placeholder: '#6b7280' },
        tagColor: { label: 'Tag Color', type: 'color', description: 'Color of blog tags', placeholder: '#3b82f6' }
      },
      gallerySectionFields: {
        backgroundColor: { label: 'Section Background Color', type: 'color', description: 'Background color of the gallery section', placeholder: '#ffffff' },
        cardBackgroundColor: { label: 'Card Background Color', type: 'color', description: 'Background color of gallery cards', placeholder: '#f9fafb' },
        cardBorderColor: { label: 'Card Border Color', type: 'color', description: 'Border color of gallery cards', placeholder: '#e5e7eb' },
        cardBorderRadius: { label: 'Card Border Radius', type: 'string', description: 'Border radius of gallery cards', placeholder: '0.5rem' },
        cardShadowColor: { label: 'Card Shadow Color', type: 'color', description: 'Shadow color of gallery cards', placeholder: 'rgba(0, 0, 0, 0.1)' },
        titleColor: { label: 'Title Color', type: 'color', description: 'Color of gallery section title', placeholder: '#1f2937' },
        textColor: { label: 'Text Color', type: 'color', description: 'Color of gallery text', placeholder: '#374151' },
        overlayColor: { label: 'Overlay Color', type: 'color', description: 'Color of image overlay on hover', placeholder: 'rgba(0, 0, 0, 0.5)' },
        iconColor: { label: 'Icon Color', type: 'color', description: 'Color of gallery icons', placeholder: '#ffffff' },
        buttonBackgroundColor: { label: 'Button Background Color', type: 'color', description: 'Background color of view all button', placeholder: '#3b82f6' },
        buttonTextColor: { label: 'Button Text Color', type: 'color', description: 'Text color of view all button', placeholder: '#ffffff' },
        buttonHoverBackgroundColor: { label: 'Button Hover Background', type: 'color', description: 'Background color on button hover', placeholder: '#2563eb' },
        buttonHoverTextColor: { label: 'Button Hover Text Color', type: 'color', description: 'Text color on button hover', placeholder: '#ffffff' }
      },
      footerSectionFields: {
        backgroundColor: { label: 'Background Color', type: 'color', description: 'Background color of the footer', placeholder: '#ffffff' },
        borderColor: { label: 'Border Color', type: 'color', description: 'Color of footer border', placeholder: '#e5e7eb' },
        shadowColor: { label: 'Shadow Color', type: 'color', description: 'Color of footer shadow', placeholder: 'rgba(0, 0, 0, 0.1)' },
        brandNameColor: { label: 'Brand Name Color', type: 'color', description: 'Color of the brand name in footer', placeholder: '#1f2937' },
        textColor: { label: 'Text Color', type: 'color', description: 'Color of footer text (contact info)', placeholder: '#6b7280' },
        copyrightColor: { label: 'Copyright Color', type: 'color', description: 'Color of copyright text', placeholder: '#6b7280' },

      },
      socialSectionFields: {
        backgroundColor: { label: 'Section Background Color', type: 'color', description: 'Background color of the social media section', placeholder: '#f9fafb' },
        titleColor: { label: 'Title Color', type: 'color', description: 'Color of the section title', placeholder: '#1f2937' },
        subtitleColor: { label: 'Subtitle Color', type: 'color', description: 'Color of the section subtitle', placeholder: '#6b7280' },
        iconColor: { label: 'Icon Color', type: 'color', description: 'Default color of social media icons', placeholder: '#3b82f6' },
        iconHoverColor: { label: 'Icon Hover Color', type: 'color', description: 'Color of social media icons on hover', placeholder: '#1d4ed8' }
      },
      sectionHeadersFields: {
        // Reviews Section
        reviewsTitle: { label: 'Reviews Section Title', type: 'string', description: 'Title for the reviews section', placeholder: 'What Our Customers Say' },
        reviewsSubtext: { label: 'Reviews Section Subtext', type: 'string', description: 'Subtext for the reviews section', placeholder: 'Read testimonials from satisfied customers' },
        
        // Appointment Section
        appointmentTitle: { label: 'Appointment Section Title', type: 'string', description: 'Title for the appointment section', placeholder: 'Book Your Appointment' },
        appointmentSubtext: { label: 'Appointment Section Subtext', type: 'string', description: 'Subtext for the appointment section', placeholder: 'Take the first step towards better service! Our team is here to help you.' },
        
        // Contact Section
        contactTitle: { label: 'Contact Section Title', type: 'string', description: 'Title for the contact section', placeholder: 'Contact Information' },
        contactSubtext: { label: 'Contact Section Subtext', type: 'string', description: 'Subtext for the contact section', placeholder: 'Get in touch with us. We\'re here to help you with all your needs.' },
        
        // Posts Section
        postsTitle: { label: 'Posts Section Title', type: 'string', description: 'Title for the posts section', placeholder: 'Our Latest Posts' },
        postsSubtext: { label: 'Posts Section Subtext', type: 'string', description: 'Subtext for the posts section', placeholder: 'Check out our recent updates and content' },
        
        // Blog Section
        blogTitle: { label: 'Blog Section Title', type: 'string', description: 'Title for the blog section', placeholder: 'Latest Blog Posts' },
        blogSubtext: { label: 'Blog Section Subtext', type: 'string', description: 'Subtext for the blog section', placeholder: 'Read our latest articles and insights' },
        
        // Gallery Section
        galleryTitle: { label: 'Gallery Section Title', type: 'string', description: 'Title for the gallery section', placeholder: 'Photo Gallery' },
        gallerySubtext: { label: 'Gallery Section Subtext', type: 'string', description: 'Subtext for the gallery section', placeholder: 'Browse through our collection of photos' },
        
        // Social Media Section
        socialTitle: { label: 'Social Media Section Title', type: 'string', description: 'Title for the social media section', placeholder: 'Find us on social media' },
        socialSubtext: { label: 'Social Media Section Subtext', type: 'string', description: 'Subtext for the social media section', placeholder: 'Connect with us and stay updated with our latest news and updates' },
        
        // Products/Services Section
        productsTitle: { label: 'Products/Services Section Title', type: 'string', description: 'Title for the products/services section', placeholder: 'Our Services' },
        productsSubtext: { label: 'Products/Services Section Subtext', type: 'string', description: 'Subtext for the products/services section', placeholder: 'Explore our range of services and offerings' }
      },


      // Subdomain management
      currentSubdomain: null,
      newSubdomain: '',
      subdomainError: '',
      subdomainAvailability: null,
      showSubdomainEditDialog: false,
      isSavingSubdomain: false,
      
      // Custom domain management
      currentCustomDomain: null,
      newCustomDomain: '',
      customDomainError: '',
      customDomainAvailability: null,
      isSavingCustomDomain: false,
      showCustomDomainDialog: false,
      
      // Subdomain check timeout
      subdomainCheckTimeout: null,
      
      // Domain management tab state
      domainManagementTab: 'subdomain',
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters.currentUser;
    },
    currentUID() {
      return this.$store.getters.currentUID;
    },
    WID() {
      return this.$store.getters.WID;
    },
    hasUnsavedChanges() {
      return (
        JSON.stringify(this.locationStyleDraft) !== JSON.stringify(this.originalLocationStyle) ||
        JSON.stringify(this.ctaAppointmentsDraft) !== JSON.stringify(this.originalCtaAppointments) ||
        JSON.stringify(this.headerStyleDraft) !== JSON.stringify(this.originalHeaderStyle) ||
        JSON.stringify(this.heroSectionDraft) !== JSON.stringify(this.originalHeroSection) ||
        JSON.stringify(this.reviewsSectionDraft) !== JSON.stringify(this.originalReviewsSection) ||
        JSON.stringify(this.contactInformationDraft) !== JSON.stringify(this.originalContactInformation) ||
        JSON.stringify(this.appointmentStylesDraft) !== JSON.stringify(this.originalAppointmentStyles) ||
        JSON.stringify(this.productStylesDraft) !== JSON.stringify(this.originalProductStyles) ||
        JSON.stringify(this.postsSectionDraft) !== JSON.stringify(this.originalPostsSection) ||
        JSON.stringify(this.blogSectionDraft) !== JSON.stringify(this.originalBlogSection) ||
        JSON.stringify(this.gallerySectionDraft) !== JSON.stringify(this.originalGallerySection) ||
        JSON.stringify(this.footerSectionDraft) !== JSON.stringify(this.originalFooterSection) ||
        JSON.stringify(this.socialSectionDraft) !== JSON.stringify(this.originalSocialSection) ||
        JSON.stringify(this.sectionHeadersDraft) !== JSON.stringify(this.originalSectionHeaders) ||
        JSON.stringify(this.productsServicesDraft) !== JSON.stringify(this.originalProductsServices) ||
        this.currentTheme !== this.originalTheme
      );
    }
  },
      watch: {
      selectedLocationId: {
        handler() {
          this.loadLocationStyle();
          this.loadSubdomainData();
          this.loadCustomDomainData();
        },
        immediate: true
      },
      domainManagementTab: {
        handler(newTab) {
          // Load appropriate data when switching between subdomain and custom domain tabs
          if (newTab === 'subdomain') {
            this.loadSubdomainData();
          } else if (newTab === 'custom-domain') {
            this.loadCustomDomainData();
          }
        },
        immediate: false
      },
    newSubdomain: {
      handler(newValue, oldValue) {
        console.log('🔍 [newSubdomain watcher] Subdomain changed from:', oldValue, 'to:', newValue);
        if (newValue) {
          // Clear any previous error and set to checking
          this.subdomainError = '';
          this.subdomainAvailability = 'checking';
          console.log('🔍 [newSubdomain watcher] Setting availability to checking and scheduling check...');
          // Check availability with a small delay to avoid too many requests
          clearTimeout(this.subdomainCheckTimeout);
          this.subdomainCheckTimeout = setTimeout(() => {
            console.log('🔍 [newSubdomain watcher] Executing availability check for:', newValue);
            this.checkSubdomainAvailability(newValue);
          }, 300);
        } else {
          this.subdomainError = '';
          this.subdomainAvailability = null;
          console.log('🔍 [newSubdomain watcher] Cleared subdomain, resetting availability');
        }
      },
      immediate: false
    },
    newCustomDomain: {
      handler(newValue) {
        if (newValue) {
          this.checkCustomDomainAvailability(newValue);
        } else {
          this.customDomainError = '';
          this.customDomainAvailability = null;
        }
      },
      immediate: false
    },
    subdomainAvailability: {
      handler(newValue) {
        console.log('🔍 [subdomainAvailability watcher] Availability changed to:', newValue);
      },
      immediate: false
    },
    activeTab() {
      // Update scroll buttons when tab changes
      this.$nextTick(() => {
        this.checkScrollButtons();
      });
    },
    'locationStyleDraft.appointments'(newValue) {
      // If appointment booking is disabled, also disable the appointment button
      if (!newValue && this.ctaAppointmentsDraft.showAppointmentButton) {
        this.ctaAppointmentsDraft.showAppointmentButton = false;
      }
    }
  },
  async mounted() {
    try {
      console.log('🚀 [mounted] Starting component initialization...');
      
      // Check if required data is available
      if (!this.currentUID || !this.WID) {
        console.error('❌ [mounted] Missing required data:', {
          currentUID: this.currentUID,
          WID: this.WID
        });
        this.$snackbar.show('Missing user or workspace data. Please refresh the page.', 'error');
        return;
      }
      
      console.log('✅ [mounted] Required data available, fetching locations...');
      await this.fetchLocations();
      
      if (this.selectedLocationId) {
        console.log('✅ [mounted] Location selected, loading location data...');
        await this.loadLocationStyle();
        await this.loadSubdomainData();
      } else {
        console.warn('⚠️ [mounted] No location selected');
      }
      
      if (this.WID) {
        console.log('✅ [mounted] Loading workspace-level configurations...');
        await this.loadCtaAppointments();
        await this.loadHeaderStyle();
        await this.loadHeroSection();
        await this.loadReviewsSection();
        await this.loadContactInformation();
        await this.loadAppointmentStyles();
        await this.loadProductStyles();
        await this.loadPostsSection();
        await this.loadBlogSection();
        await this.loadGallerySection();
        await this.loadFooterSection();
        await this.loadSocialSection();
        await this.loadSectionHeaders();
        await this.loadProductsServices();
        
        await this.loadTheme();
      }
      
      // Refresh the store preview with initial data after all configurations are loaded
      this.$nextTick(() => {
        this.refreshStorePreview();
        // Initialize tab scroll buttons
        this.checkScrollButtons();
      });
      
      console.log('✅ [mounted] Component initialization completed');
    } catch (error) {
      console.error('❌ [mounted] Error during initialization:', error);
      this.$snackbar.show('Error initializing component. Please refresh the page.', 'error');
    }
  },
  methods: {
    async refreshPreview() {
      try {
        this.isRefreshingPreview = true;
        
        // Force re-render of the IndividualStore component by changing the key
        this.storeRefreshKey++;
        
        // Add a small delay to show the refresh animation
        await new Promise(resolve => setTimeout(resolve, 500));
        
        this.$snackbar.show('Preview refreshed successfully!', 'success');
      } catch (error) {
        console.error('Error refreshing preview:', error);
        this.$snackbar.show('Error refreshing preview', 'error');
      } finally {
        this.isRefreshingPreview = false;
      }
    },
    
    // Tab scroll functionality
    checkScrollButtons() {
      if (!this.$refs.tabsContainer) return;
      
      const container = this.$refs.tabsContainer;
      this.canScrollLeft = container.scrollLeft > 0;
      this.canScrollRight = container.scrollLeft < (container.scrollWidth - container.clientWidth);
    },
    
    scrollTabs(direction) {
      if (!this.$refs.tabsContainer) return;
      
      const container = this.$refs.tabsContainer;
      const scrollAmount = 200; // Scroll by 200px
      
      if (direction === 'left') {
        container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else if (direction === 'right') {
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
      
      // Update button visibility after scroll
      setTimeout(() => this.checkScrollButtons(), 300);
    },
    
    getSubdomainUrl(subdomainName, type = 'landing') {
      return getSubdomainUrl(subdomainName, type);
    },
    getDomainConfig() {
      return getDomainConfig();
    },
    async fetchLocations() {
      if (!this.currentUID || !this.WID) {
        console.error('❌ [fetchLocations] Missing required data:', {
          currentUID: this.currentUID,
          WID: this.WID
        });
        return;
      }
      
      try {
        console.log('🔍 [fetchLocations] Starting location fetch...');
        console.log('🔍 [fetchLocations] Looking for locations with admin field == WID:', this.WID);
        
        // Query for locations that belong to this workspace (admin field == WID)
        const locationsCol = collection(db, 'users', this.currentUID, 'fly_workspaces');
        const locationsQuery = query(
          locationsCol, 
          where("admin", "==", this.WID),
          where("storeWorkspace", "==", true)
        );
        const snapshot = await getDocs(locationsQuery);
        
        console.log('🔍 [fetchLocations] Raw documents found:', snapshot.docs.length);
        
        // Map all documents first
        const allLocations = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        }));
        
        console.log('🔍 [fetchLocations] All locations with admin == WID:', allLocations.map(loc => ({
          id: loc.id,
          type: loc.type,
          name: loc.name,
          storeLocation: loc.storeLocation,
          gmbProfile: !!loc.gmbProfile,
          gmb_shallow_profile_data: !!loc.gmb_shallow_profile_data,
          admin: loc.admin
        })));
        
        // Filter locations - only include actual location documents (not workspace documents)
        this.locations = allLocations.filter(loc => {
          const hasStoreWorkspace = loc?.storeWorkspace === true;
          const isAdminMatch = loc?.admin === this.WID;
          
          const shouldInclude = hasStoreWorkspace && isAdminMatch;
          
          console.log('🔍 [fetchLocations] Location filter check:', {
            id: loc.id,
            hasStoreWorkspace,
            isAdminMatch,
            shouldInclude,
            workspaceData: loc.workspaceData
          });
          
          return shouldInclude;
        });
        
        console.log('✅ [fetchLocations] Filtered locations (excluding workspace):', this.locations.length);
        
        if (this.locations.length > 0) {
          this.selectedLocationId = this.locations[0].id;
          this.selectedLocation = this.locations[0];
          console.log('✅ [fetchLocations] Selected first location:', {
            id: this.selectedLocationId,
            name: this.selectedLocation.name || this.selectedLocation.storeLocation
          });
        } else {
          console.warn('⚠️ [fetchLocations] No locations found after filtering');
          // Try to get the workspace itself as a fallback
          const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
          const workspaceDoc = await getDoc(workspaceRef);
          if (workspaceDoc.exists()) {
            const workspaceData = workspaceDoc.data();
            this.locations = [{
              id: this.WID,
              name: workspaceData.name || 'Main Workspace',
              storeLocation: workspaceData.name || 'Main Workspace',
              type: 'workspace'
            }];
            this.selectedLocationId = this.WID;
            this.selectedLocation = this.locations[0];
            console.log('✅ [fetchLocations] Using workspace as fallback location');
          }
        }
      } catch (error) {
        console.error('❌ [fetchLocations] Error fetching locations:', error);
        this.$snackbar.show('Error loading locations. Please try refreshing the page.', 'error');
      }
    },
    onLocationChange() {
      this.selectedLocation = this.locations.find(loc => loc.id === this.selectedLocationId);
      this.loadLocationStyle();
      this.loadHeaderStyle();
      this.loadHeroSection();
      this.loadReviewsSection();
      this.loadContactInformation();
      this.loadAppointmentStyles();
      this.loadProductStyles();
      this.loadPostsSection();
      this.loadBlogSection();
      this.loadGallerySection();
      this.loadFooterSection();
      this.loadSocialSection();
      
      // Refresh the store preview with new location data
      this.refreshStorePreview();
    },
    getSlugForLocation(location) {
      if (!location) return '';
      const name = location.name || location.storeLocation || '';
      return name.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    },
    async loadLocationStyle() {
      if (!this.selectedLocationId) return;
      
      // Initialize with defaults - only appointments boolean
      this.locationStyleDraft = {
        appointments: false
      };
      
      // Load from individual store configuration (always at location level)
        const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.selectedLocationId);
        const locationDoc = await getDoc(locationRef);
      if (locationDoc.exists() && locationDoc.data().location_style) {
        this.locationStyleDraft = { ...this.locationStyleDraft, ...locationDoc.data().location_style };
      }
      
      this.originalLocationStyle = JSON.parse(JSON.stringify(this.locationStyleDraft));
    },
    async loadCtaAppointments() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.ctaAppointmentsDraft = {};
      Object.keys(this.ctaAppointmentsFields).forEach(key => {
        const field = this.ctaAppointmentsFields[key];
        if (field.type === 'boolean') {
          this.ctaAppointmentsDraft[key] = field.placeholder;
        } else {
          this.ctaAppointmentsDraft[key] = field.placeholder;
        }
      });
      
      // Load from workspace-level configuration
      const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
      const workspaceDoc = await getDoc(workspaceRef);
      if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.cta_appointments) {
        this.ctaAppointmentsDraft = { ...this.ctaAppointmentsDraft, ...workspaceDoc.data().location_common_styles.cta_appointments };
      }
      
      this.originalCtaAppointments = JSON.parse(JSON.stringify(this.ctaAppointmentsDraft));
    },
    
    async loadHeaderStyle() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.headerStyleDraft = {};
      Object.keys(this.headerStyleFields).forEach(key => {
        const field = this.headerStyleFields[key];
        if (field.type === 'boolean') {
          this.headerStyleDraft[key] = field.placeholder;
        } else if (field.type === 'color') {
          this.headerStyleDraft[key] = field.placeholder;
        } else {
          this.headerStyleDraft[key] = field.placeholder;
        }
      });
      
      // Load from workspace-level configuration
      const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
      const workspaceDoc = await getDoc(workspaceRef);
      if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.header_styles) {
        this.headerStyleDraft = { ...this.headerStyleDraft, ...workspaceDoc.data().location_common_styles.header_styles };
      }
      
      this.originalHeaderStyle = JSON.parse(JSON.stringify(this.headerStyleDraft));
    },
    async loadHeroSection() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.heroSectionDraft = {};
      Object.keys(this.heroSectionFields).forEach(key => {
        const field = this.heroSectionFields[key];
        if (field.type === 'color') {
          this.heroSectionDraft[key] = field.placeholder;
        } else {
          this.heroSectionDraft[key] = field.placeholder;
        }
      });
      
      // Load from workspace-level configuration
      const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
      const workspaceDoc = await getDoc(workspaceRef);
      if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.hero_section) {
        this.heroSectionDraft = { ...this.heroSectionDraft, ...workspaceDoc.data().location_common_styles.hero_section };
      }
      
      this.originalHeroSection = JSON.parse(JSON.stringify(this.heroSectionDraft));
    },

    async loadReviewsSection() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.reviewsSectionDraft = {};
      Object.keys(this.reviewsSectionFields).forEach(key => {
        const field = this.reviewsSectionFields[key];
        if (field.type === 'color') {
          this.reviewsSectionDraft[key] = field.placeholder;
        } else {
          this.reviewsSectionDraft[key] = field.placeholder;
        }
      });
      
      // Load from workspace-level configuration
      const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
      const workspaceDoc = await getDoc(workspaceRef);
      if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.reviews_section) {
        this.reviewsSectionDraft = { ...this.reviewsSectionDraft, ...workspaceDoc.data().location_common_styles.reviews_section };
      }
      
      this.originalReviewsSection = JSON.parse(JSON.stringify(this.reviewsSectionDraft));
    },
    async loadContactInformation() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.contactInformationDraft = {};
      Object.keys(this.contactInformationFields).forEach(key => {
        const field = this.contactInformationFields[key];
        if (field.type === 'color') {
          this.contactInformationDraft[key] = field.placeholder;
        } else {
          this.contactInformationDraft[key] = field.placeholder;
        }
      });
      
      // Load from workspace-level configuration
      const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
      const workspaceDoc = await getDoc(workspaceRef);
      if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.contact_information) {
        this.contactInformationDraft = { ...this.contactInformationDraft, ...workspaceDoc.data().location_common_styles.contact_information };
      }
      
      this.originalContactInformation = JSON.parse(JSON.stringify(this.contactInformationDraft));
    },
    async loadAppointmentStyles() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.appointmentStylesDraft = {};
      Object.keys(this.appointmentStylesFields).forEach(key => {
        const field = this.appointmentStylesFields[key];
        if (field.type === 'color') {
          this.appointmentStylesDraft[key] = field.placeholder;
        } else {
          this.appointmentStylesDraft[key] = field.placeholder;
        }
      });
      
      // Load from workspace-level configuration
      const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
      const workspaceDoc = await getDoc(workspaceRef);
      if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.appointment_styles) {
        this.appointmentStylesDraft = { ...this.appointmentStylesDraft, ...workspaceDoc.data().location_common_styles.appointment_styles };
      }
      
      this.originalAppointmentStyles = JSON.parse(JSON.stringify(this.appointmentStylesDraft));
    },
    async loadProductStyles() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.productStylesDraft = {};
      Object.keys(this.productStylesFields).forEach(key => {
        const field = this.productStylesFields[key];
        if (field.type === 'color') {
          this.productStylesDraft[key] = field.placeholder;
        } else {
          this.productStylesDraft[key] = field.placeholder;
        }
      });
      
      // Load from workspace-level configuration
      const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
      const workspaceDoc = await getDoc(workspaceRef);
      if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.product_styles) {
        this.productStylesDraft = { ...this.productStylesDraft, ...workspaceDoc.data().location_common_styles.product_styles };
      }
      
      this.originalProductStyles = JSON.parse(JSON.stringify(this.productStylesDraft));
    },
    async loadPostsSection() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.postsSectionDraft = {};
      Object.keys(this.postsSectionFields).forEach(key => {
        const field = this.postsSectionFields[key];
        if (field.type === 'color') {
          this.postsSectionDraft[key] = field.placeholder;
        } else {
          this.postsSectionDraft[key] = field.placeholder;
        }
      });
      
      // Load from workspace-level configuration
      const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
      const workspaceDoc = await getDoc(workspaceRef);
      if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.posts_section) {
        this.postsSectionDraft = { ...this.postsSectionDraft, ...workspaceDoc.data().location_common_styles.posts_section };
      }
      
      this.originalPostsSection = JSON.parse(JSON.stringify(this.postsSectionDraft));
    },
    async loadBlogSection() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.blogSectionDraft = {};
      Object.keys(this.blogSectionFields).forEach(key => {
        const field = this.blogSectionFields[key];
        if (field.type === 'color') {
          this.blogSectionDraft[key] = field.placeholder;
        } else {
          this.blogSectionDraft[key] = field.placeholder;
        }
      });
      
      // Load from workspace-level configuration
      const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
      const workspaceDoc = await getDoc(workspaceRef);
      if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.blog_section) {
        this.blogSectionDraft = { ...this.blogSectionDraft, ...workspaceDoc.data().location_common_styles.blog_section };
      }
      
      this.originalBlogSection = JSON.parse(JSON.stringify(this.blogSectionDraft));
    },
    async loadGallerySection() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.gallerySectionDraft = {};
      Object.keys(this.gallerySectionFields).forEach(key => {
        const field = this.gallerySectionFields[key];
        if (field.type === 'color') {
          this.gallerySectionDraft[key] = field.placeholder;
        } else {
          this.gallerySectionDraft[key] = field.placeholder;
        }
      });
      
      // Load from workspace-level configuration
      const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
      const workspaceDoc = await getDoc(workspaceRef);
      if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.gallery_section) {
        this.gallerySectionDraft = { ...this.gallerySectionDraft, ...workspaceDoc.data().location_common_styles.gallery_section };
      }
      
      this.originalGallerySection = JSON.parse(JSON.stringify(this.gallerySectionDraft));
    },
    async loadFooterSection() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.footerSectionDraft = {};
      Object.keys(this.footerSectionFields).forEach(key => {
        const field = this.footerSectionFields[key];
        if (field.type === 'color') {
          this.footerSectionDraft[key] = field.placeholder;
        } else {
          this.footerSectionDraft[key] = field.placeholder;
        }
      });
      
      // Load from workspace-level configuration
      const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
      const workspaceDoc = await getDoc(workspaceRef);
      if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.footer_section) {
        this.footerSectionDraft = { ...this.footerSectionDraft, ...workspaceDoc.data().location_common_styles.footer_section };
      }
      
      this.originalFooterSection = JSON.parse(JSON.stringify(this.footerSectionDraft));
    },
    async loadSocialSection() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.socialSectionDraft = {};
      Object.keys(this.socialSectionFields).forEach(key => {
        const field = this.socialSectionFields[key];
        if (field.type === 'color') {
          this.socialSectionDraft[key] = field.placeholder;
        } else {
          this.socialSectionDraft[key] = field.placeholder;
        }
      });
      
      // Load from workspace-level configuration
      const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
      const workspaceDoc = await getDoc(workspaceRef);
      if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.social_section) {
        this.socialSectionDraft = { ...this.socialSectionDraft, ...workspaceDoc.data().location_common_styles.social_section };
      }
      
      this.originalSocialSection = JSON.parse(JSON.stringify(this.socialSectionDraft));
    },
    async loadSectionHeaders() {
      if (!this.WID) return;
      
      // Initialize with defaults
      this.sectionHeadersDraft = {};
      Object.keys(this.sectionHeadersFields).forEach(key => {
        const field = this.sectionHeadersFields[key];
        this.sectionHeadersDraft[key] = field.placeholder;
      });
      
      // Load from workspace-level configuration
      const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
      const workspaceDoc = await getDoc(workspaceRef);
      if (workspaceDoc.exists() && workspaceDoc.data().location_common_styles?.section_headers) {
        this.sectionHeadersDraft = { ...this.sectionHeadersDraft, ...workspaceDoc.data().location_common_styles.section_headers };
      }
      
      this.originalSectionHeaders = JSON.parse(JSON.stringify(this.sectionHeadersDraft));
    },

    async loadProductsServices() {
      if (!this.WID) return;
      
      // Initialize with empty array
      this.productsServicesDraft = [];
      
      // Load from workspace-level configuration (admin level)
      const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
      const workspaceDoc = await getDoc(workspaceRef);
      if (workspaceDoc.exists() && workspaceDoc.data().products_services) {
        this.productsServicesDraft = [...workspaceDoc.data().products_services];
      }
      
      this.originalProductsServices = JSON.parse(JSON.stringify(this.productsServicesDraft));
    },

    handleSaveClick() {
      this.showSaveDialog = true;
    },
    async confirmSave() {
      this.showSaveDialog = false;
      await this.saveConfiguration();
    },
    async saveConfiguration() {
      try {
        // Save workspace-level configuration (applies to all stores)
        if (this.WID) {
          const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
          const workspaceDoc = await getDoc(workspaceRef);
          let commonStyles = workspaceDoc.data().location_common_styles || {};
          
          // Save all configuration types at workspace level
          commonStyles = {
            ...commonStyles,
            // CTA & Appointments
            cta_appointments: this.ctaAppointmentsDraft,
            // Header styles
            header_styles: this.headerStyleDraft,
            // Hero section styles
            hero_section: this.heroSectionDraft,
            // Reviews section styles
            reviews_section: this.reviewsSectionDraft,
            // Contact information styles
            contact_information: this.contactInformationDraft,
            // Appointment styles
            appointment_styles: this.appointmentStylesDraft,
            // Product styles
            product_styles: this.productStylesDraft,
            // Posts section styles
            posts_section: this.postsSectionDraft,
            // Blog section styles
            blog_section: this.blogSectionDraft,
            // Gallery section styles
            gallery_section: this.gallerySectionDraft,
            // Footer section styles
            footer_section: this.footerSectionDraft,
            // Social section styles
            social_section: this.socialSectionDraft,
            // Section headers
            section_headers: this.sectionHeadersDraft
          };
          
          await updateDoc(workspaceRef, { 
            location_common_styles: commonStyles,
            current_theme: this.currentTheme,
            theme_draft: this.themeDraft
          });
        }
        
        // Save products/services data at workspace level (admin level)
        if (this.WID) {
          const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
          await updateDoc(workspaceRef, { 
            products_services: this.productsServicesDraft
          });
        }
        
        // Also save individual store configuration for location-specific settings
        if (this.selectedLocationId) {
          const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.selectedLocationId);
          await updateDoc(locationRef, { 
            location_style: this.locationStyleDraft // Contains appointments boolean and location-specific settings
          });
        }
        
        // Update original configs after successful save
        this.originalLocationStyle = JSON.parse(JSON.stringify(this.locationStyleDraft));
        this.originalCtaAppointments = JSON.parse(JSON.stringify(this.ctaAppointmentsDraft));
        this.originalHeaderStyle = JSON.parse(JSON.stringify(this.headerStyleDraft));
        this.originalHeroSection = JSON.parse(JSON.stringify(this.heroSectionDraft));
        this.originalReviewsSection = JSON.parse(JSON.stringify(this.reviewsSectionDraft));
        this.originalContactInformation = JSON.parse(JSON.stringify(this.contactInformationDraft));
        this.originalAppointmentStyles = JSON.parse(JSON.stringify(this.appointmentStylesDraft));
        this.originalProductStyles = JSON.parse(JSON.stringify(this.productStylesDraft));
        this.originalPostsSection = JSON.parse(JSON.stringify(this.postsSectionDraft));
        this.originalBlogSection = JSON.parse(JSON.stringify(this.blogSectionDraft));
        this.originalFooterSection = JSON.parse(JSON.stringify(this.footerSectionDraft));
        this.originalSocialSection = JSON.parse(JSON.stringify(this.socialSectionDraft));
        this.originalSectionHeaders = JSON.parse(JSON.stringify(this.sectionHeadersDraft));
        this.originalProductsServices = JSON.parse(JSON.stringify(this.productsServicesDraft));
        this.originalTheme = this.currentTheme;
        
        // Refresh the IndividualStore component to fetch latest data
        this.refreshStorePreview();
        
        this.$snackbar.show(
          'Configuration saved successfully!', 
          'success'
        );
      } catch (error) {
        console.error('Error saving configuration:', error);
        this.$snackbar.show('Error saving configuration', 'error');
      }
    },
    handleBackClick() {
      if (this.hasUnsavedChanges) {
        this.showBackDialog = true;
      } else {
        this.goBack();
      }
    },
    confirmBack() {
      this.showBackDialog = false;
      this.goBack();
    },
    goBack() {
      this.$router.go(-1);
    },
    revertAllChanges() {
      if (this.originalLocationStyle) {
        this.locationStyleDraft = JSON.parse(JSON.stringify(this.originalLocationStyle));
      }
      if (this.originalCtaAppointments) {
        this.ctaAppointmentsDraft = JSON.parse(JSON.stringify(this.originalCtaAppointments));
      }
      if (this.originalHeaderStyle) {
        this.headerStyleDraft = JSON.parse(JSON.stringify(this.originalHeaderStyle));
      }
      if (this.originalHeroSection) {
        this.heroSectionDraft = JSON.parse(JSON.stringify(this.originalHeroSection));
      }
      if (this.originalReviewsSection) {
        this.reviewsSectionDraft = JSON.parse(JSON.stringify(this.originalReviewsSection));
      }
      if (this.originalContactInformation) {
        this.contactInformationDraft = JSON.parse(JSON.stringify(this.originalContactInformation));
      }
      if (this.originalAppointmentStyles) {
        this.appointmentStylesDraft = JSON.parse(JSON.stringify(this.originalAppointmentStyles));
      }
      if (this.originalProductStyles) {
        this.productStylesDraft = JSON.parse(JSON.stringify(this.originalProductStyles));
      }
      if (this.originalPostsSection) {
        this.postsSectionDraft = JSON.parse(JSON.stringify(this.originalPostsSection));
      }
      if (this.originalBlogSection) {
        this.blogSectionDraft = JSON.parse(JSON.stringify(this.originalBlogSection));
      }
      if (this.originalFooterSection) {
        this.footerSectionDraft = JSON.parse(JSON.stringify(this.originalFooterSection));
      }
      if (this.originalSocialSection) {
        this.socialSectionDraft = JSON.parse(JSON.stringify(this.originalSocialSection));
      }
      if (this.originalProductsServices) {
        this.productsServicesDraft = JSON.parse(JSON.stringify(this.originalProductsServices));
      }

      if (this.originalTheme) {
        this.currentTheme = this.originalTheme;
      }
      this.$snackbar.show('All changes reverted!', 'success');
    },
    resetToDefaults() {
      // Reset location styles to defaults
      Object.keys(this.locationStyleFields).forEach(key => {
        const field = this.locationStyleFields[key];
        if (field.type === 'boolean') {
          this.locationStyleDraft[key] = false;
        } else {
          this.locationStyleDraft[key] = field.placeholder;
        }
      });
      
      // Reset CTA & Appointments to defaults
      Object.keys(this.ctaAppointmentsFields).forEach(key => {
        const field = this.ctaAppointmentsFields[key];
        if (field.type === 'boolean') {
          this.ctaAppointmentsDraft[key] = field.placeholder;
        } else {
          this.ctaAppointmentsDraft[key] = field.placeholder;
        }
      });
      
      // Reset header styles to defaults
      Object.keys(this.headerStyleFields).forEach(key => {
        const field = this.headerStyleFields[key];
        if (field.type === 'boolean') {
          this.headerStyleDraft[key] = field.placeholder;
        } else if (field.type === 'array') {
          this.headerStyleDraft[key] = []; // Reset arrays to empty
        } else {
          this.headerStyleDraft[key] = field.placeholder;
        }
      });
      
      // Reset hero section styles to defaults
      Object.keys(this.heroSectionFields).forEach(key => {
        const field = this.heroSectionFields[key];
        this.heroSectionDraft[key] = field.placeholder;
      });
      
      // Reset reviews section styles to defaults
      Object.keys(this.reviewsSectionFields).forEach(key => {
        const field = this.reviewsSectionFields[key];
        this.reviewsSectionDraft[key] = field.placeholder;
      });
      
      // Reset contact information styles to defaults
      Object.keys(this.contactInformationFields).forEach(key => {
        const field = this.contactInformationFields[key];
        this.contactInformationDraft[key] = field.placeholder;
      });
      
      // Reset appointment styles to defaults
      Object.keys(this.appointmentStylesFields).forEach(key => {
        const field = this.appointmentStylesFields[key];
        this.appointmentStylesDraft[key] = field.placeholder;
      });
      
      // Reset product styles to defaults
      Object.keys(this.productStylesFields).forEach(key => {
        const field = this.productStylesFields[key];
        this.productStylesDraft[key] = field.placeholder;
      });
      
      // Reset posts section styles to defaults
      Object.keys(this.postsSectionFields).forEach(key => {
        const field = this.postsSectionFields[key];
        this.postsSectionDraft[key] = field.placeholder;
      });
      
      // Reset blog section styles to defaults
      Object.keys(this.blogSectionFields).forEach(key => {
        const field = this.blogSectionFields[key];
        this.blogSectionDraft[key] = field.placeholder;
      });
      
      // Reset footer section styles to defaults
      Object.keys(this.footerSectionFields).forEach(key => {
        const field = this.footerSectionFields[key];
        this.footerSectionDraft[key] = field.placeholder;
      });
      
      // Reset social section styles to defaults
      Object.keys(this.socialSectionFields).forEach(key => {
        const field = this.socialSectionFields[key];
        this.socialSectionDraft[key] = field.placeholder;
      });
      
      // Reset section headers to defaults
      Object.keys(this.sectionHeadersFields).forEach(key => {
        const field = this.sectionHeadersFields[key];
        this.sectionHeadersDraft[key] = field.placeholder;
      });
      

      

      
      this.$snackbar.show('Reset to defaults!', 'success');
    },
    refreshStorePreview() {
      // Increment the key to force Vue to re-render the IndividualStore component
      this.storeRefreshKey++;
      
      // Also trigger a manual refresh if the component reference exists
      this.$nextTick(() => {
        if (this.$refs.individualStoreComponent && this.$refs.individualStoreComponent.fetchStoreData) {
          this.$refs.individualStoreComponent.fetchStoreData();
        }
      });
    },




    async loadTheme() {
      if (!this.WID) return;
      
      try {
        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        const workspaceDoc = await getDoc(workspaceRef);
        
        if (workspaceDoc.exists()) {
          const data = workspaceDoc.data();
          this.currentTheme = data.current_theme || 'default';
          this.themeDraft = data.theme_draft || {};
        } else {
          this.currentTheme = 'default';
          this.themeDraft = {};
        }
        
        this.originalTheme = this.currentTheme;
      } catch (error) {
        console.error('Error loading theme:', error);
        this.currentTheme = 'default';
        this.themeDraft = {};
        this.originalTheme = 'default';
      }
    },

    selectTheme(themeKey) {
      this.currentTheme = themeKey;
    },

    applyTheme(themeKey) {
      const theme = this.predefinedThemes[themeKey];
      if (!theme) return;

      // Apply theme variables to all configuration drafts
      Object.entries(theme.variables).forEach(([variable, value]) => {
        // Map CSS variables to configuration fields
        this.mapThemeVariableToConfig(variable, value);
      });

      this.currentTheme = themeKey;
      
      // Refresh the store preview
      this.refreshStorePreview();
      
      this.$snackbar.show(`Applied ${theme.name} theme!`, 'success');
    },

    mapThemeVariableToConfig(variable, value) {
      // Map CSS variables to configuration fields based on the actual field names in the configuration
      const variableMappings = {
        // Header styles
        '--header-text-color': { target: 'headerStyleDraft', field: 'textColor' },
        '--header-background-color': { target: 'headerStyleDraft', field: 'backgroundColor' },
        '--primary-title-color': { target: 'headerStyleDraft', field: 'primaryTitleColor' },
        
        // Hero section
        '--hero-brand-name-color': { target: 'heroSectionDraft', field: 'brandNameColor' },
        '--hero-description-color': { target: 'heroSectionDraft', field: 'descriptionColor' },
        '--hero-section-bg-color': { target: 'heroSectionDraft', field: 'backgroundColor' },
        
        // Reviews section
        '--reviews-section-bg-color': { target: 'reviewsSectionDraft', field: 'backgroundColor' },
        '--review-card-bg-color': { target: 'reviewsSectionDraft', field: 'cardBackgroundColor' },
        '--review-card-text-color': { target: 'reviewsSectionDraft', field: 'cardTextColor' },
        
        // Contact section
        '--contact-section-bg-color': { target: 'contactInformationDraft', field: 'backgroundColor' },
        '--contact-card-bg-color': { target: 'contactInformationDraft', field: 'cardBackgroundColor' },
        '--contact-card-border-color': { target: 'contactInformationDraft', field: 'cardBorderColor' },
        '--contact-card-border-radius': { target: 'contactInformationDraft', field: 'cardBorderRadius' },
        '--contact-text-color': { target: 'contactInformationDraft', field: 'textColor' },
        '--contact-label-color': { target: 'contactInformationDraft', field: 'labelColor' },
        '--contact-link-color': { target: 'contactInformationDraft', field: 'linkColor' },
        '--contact-description-color': { target: 'contactInformationDraft', field: 'descriptionColor' },
        
        // Appointment styles
        '--appointment-button-bg-color': { target: 'appointmentStylesDraft', field: 'buttonBackgroundColor' },
        '--appointment-button-text-color': { target: 'appointmentStylesDraft', field: 'buttonTextColor' },
        '--appointment-button-hover-bg-color': { target: 'appointmentStylesDraft', field: 'buttonHoverBackgroundColor' },
        '--appointment-button-hover-text-color': { target: 'appointmentStylesDraft', field: 'buttonHoverTextColor' },
        '--appointment-section-bg-color': { target: 'appointmentStylesDraft', field: 'sectionBackgroundColor' },
        '--appointment-title-color': { target: 'appointmentStylesDraft', field: 'titleColor' },
        '--appointment-description-color': { target: 'appointmentStylesDraft', field: 'descriptionColor' },
        '--appointment-form-bg-color': { target: 'appointmentStylesDraft', field: 'formBackgroundColor' },
        '--appointment-form-title-color': { target: 'appointmentStylesDraft', field: 'formTitleColor' },
        '--appointment-label-color': { target: 'appointmentStylesDraft', field: 'labelColor' },
        
        // Product styles
        '--product-section-bg-color': { target: 'productStylesDraft', field: 'sectionBackgroundColor' },
        '--product-card-bg-color': { target: 'productStylesDraft', field: 'cardBackgroundColor' },
        '--product-card-border-color': { target: 'productStylesDraft', field: 'cardBorderColor' },
        
        // Posts section
        '--posts-section-bg-color': { target: 'postsSectionDraft', field: 'backgroundColor' },
        '--post-card-bg-color': { target: 'postsSectionDraft', field: 'cardBackgroundColor' },
        '--post-card-border-color': { target: 'postsSectionDraft', field: 'cardBorderColor' },
        '--post-title-color': { target: 'postsSectionDraft', field: 'titleColor' },
        '--post-text-color': { target: 'postsSectionDraft', field: 'textColor' },
        '--post-meta-color': { target: 'postsSectionDraft', field: 'metaColor' },
        
        // Blog section
        '--blog-section-bg-color': { target: 'blogSectionDraft', field: 'backgroundColor' },
        '--blog-card-bg-color': { target: 'blogSectionDraft', field: 'cardBackgroundColor' },
        '--blog-card-border-color': { target: 'blogSectionDraft', field: 'cardBorderColor' },
        '--blog-title-color': { target: 'blogSectionDraft', field: 'titleColor' },
        '--blog-text-color': { target: 'blogSectionDraft', field: 'textColor' },
        '--blog-meta-color': { target: 'blogSectionDraft', field: 'metaColor' },
        '--blog-excerpt-color': { target: 'blogSectionDraft', field: 'excerptColor' },
        '--blog-tag-color': { target: 'blogSectionDraft', field: 'tagColor' },
        
        // Footer section
        '--footer-bg-color': { target: 'footerSectionDraft', field: 'backgroundColor' },
        '--footer-border-color': { target: 'footerSectionDraft', field: 'borderColor' },
        '--footer-shadow-color': { target: 'footerSectionDraft', field: 'shadowColor' },
        '--footer-brand-name-color': { target: 'footerSectionDraft', field: 'brandNameColor' },
        '--footer-text-color': { target: 'footerSectionDraft', field: 'textColor' },
        '--footer-copyright-color': { target: 'footerSectionDraft', field: 'copyrightColor' },

        
        // Social section
        '--social-section-bg-color': { target: 'socialSectionDraft', field: 'backgroundColor' },
        '--social-title-color': { target: 'socialSectionDraft', field: 'titleColor' },
        '--social-subtitle-color': { target: 'socialSectionDraft', field: 'subtitleColor' },
        '--social-icon-color': { target: 'socialSectionDraft', field: 'iconColor' },
        '--social-icon-hover-color': { target: 'socialSectionDraft', field: 'iconHoverColor' },
        

      };

      const mapping = variableMappings[variable];
      if (mapping && this[mapping.target]) {
        this[mapping.target][mapping.field] = value;
      }
    },

    resetToCustomTheme() {
      this.currentTheme = 'custom';
      
      // Reset all configuration drafts to their original values
      if (this.originalHeaderStyle) {
        this.headerStyleDraft = JSON.parse(JSON.stringify(this.originalHeaderStyle));
      }
      if (this.originalHeroSection) {
        this.heroSectionDraft = JSON.parse(JSON.stringify(this.originalHeroSection));
      }
      if (this.originalReviewsSection) {
        this.reviewsSectionDraft = JSON.parse(JSON.stringify(this.originalReviewsSection));
      }
      if (this.originalContactInformation) {
        this.contactInformationDraft = JSON.parse(JSON.stringify(this.originalContactInformation));
      }
      if (this.originalAppointmentStyles) {
        this.appointmentStylesDraft = JSON.parse(JSON.stringify(this.originalAppointmentStyles));
      }
      if (this.originalProductStyles) {
        this.productStylesDraft = JSON.parse(JSON.stringify(this.originalProductStyles));
      }
      if (this.originalPostsSection) {
        this.postsSectionDraft = JSON.parse(JSON.stringify(this.originalPostsSection));
      }
      if (this.originalBlogSection) {
        this.blogSectionDraft = JSON.parse(JSON.stringify(this.originalBlogSection));
      }
      if (this.originalFooterSection) {
        this.footerSectionDraft = JSON.parse(JSON.stringify(this.originalFooterSection));
      }
      if (this.originalSocialSection) {
        this.socialSectionDraft = JSON.parse(JSON.stringify(this.originalSocialSection));
      }

      
      // Refresh the store preview
      this.refreshStorePreview();
      
      this.$snackbar.show('Reset to custom theme! You can now manually adjust individual settings.', 'success');
    },
    
    async enableAppointmentsForAllLocations() {
      if (!this.currentUID || !this.WID) {
        this.$snackbar.show('Missing required data to enable appointments', 'error');
        return;
      }

      this.isEnablingAllAppointments = true;
      
      try {
        // Get all locations in the workspace
        const locationsCol = collection(db, 'users', this.currentUID, 'fly_workspaces');
        const snapshot = await getDocs(locationsCol);
        const allLocations = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(loc => loc.type === 'location' || loc.gmbProfile || loc.gmb_shallow_profile_data);
        
        if (allLocations.length === 0) {
          this.$snackbar.show('No locations found to update', 'warning');
          return;
        }

        // Update each location's appointment setting
        const updatePromises = allLocations.map(async (location) => {
          const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', location.id);
          
          // Get current location data to preserve existing settings
          const locationDoc = await getDoc(locationRef);
          const currentData = locationDoc.exists() ? locationDoc.data() : {};
          
          // Update location_style to enable appointments
          const updatedLocationStyle = {
            ...currentData.location_style,
            appointments: true
          };
          
          return updateDoc(locationRef, {
            location_style: updatedLocationStyle
          });
        });

        // Wait for all updates to complete
        await Promise.all(updatePromises);
        
        // Update the current location style draft if it's one of the updated locations
        if (this.selectedLocationId && allLocations.find(loc => loc.id === this.selectedLocationId)) {
          this.locationStyleDraft.appointments = true;
          this.originalLocationStyle = JSON.parse(JSON.stringify(this.locationStyleDraft));
        }
        
        // Refresh the locations list to get updated data
        await this.fetchLocations();
        
        // Refresh the store preview
        this.refreshStorePreview();
        
        this.$snackbar.show(
          `Successfully enabled appointments for ${allLocations.length} location${allLocations.length > 1 ? 's' : ''}!`, 
          'success'
        );
        
      } catch (error) {
        console.error('Error enabling appointments for all locations:', error);
        this.$snackbar.show('Error enabling appointments for all locations. Please try again.', 'error');
      } finally {
        this.isEnablingAllAppointments = false;
      }
    },
    
    async disableAppointmentsForAllLocations() {
      if (!this.currentUID || !this.WID) {
        this.$snackbar.show('Missing required data to disable appointments', 'error');
        return;
      }

      this.isDisablingAllAppointments = true;
      
      try {
        // Get all locations in the workspace
        const locationsCol = collection(db, 'users', this.currentUID, 'fly_workspaces');
        const snapshot = await getDocs(locationsCol);
        const allLocations = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(loc => loc.type === 'location' || loc.gmbProfile || loc.gmb_shallow_profile_data);
        
        if (allLocations.length === 0) {
          this.$snackbar.show('No locations found to update', 'warning');
          return;
        }

        // Update each location's appointment setting
        const updatePromises = allLocations.map(async (location) => {
          const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', location.id);
          
          // Get current location data to preserve existing settings
          const locationDoc = await getDoc(locationRef);
          const currentData = locationDoc.exists() ? locationDoc.data() : {};
          
          // Update location_style to disable appointments
          const updatedLocationStyle = {
            ...currentData.location_style,
            appointments: false
          };
          
          return updateDoc(locationRef, {
            location_style: updatedLocationStyle
          });
        });

        // Wait for all updates to complete
        await Promise.all(updatePromises);
        
        // Update the current location style draft if it's one of the updated locations
        if (this.selectedLocationId && allLocations.find(loc => loc.id === this.selectedLocationId)) {
          this.locationStyleDraft.appointments = false;
          this.originalLocationStyle = JSON.parse(JSON.stringify(this.locationStyleDraft));
        }
        
        // Refresh the locations list to get updated data
        await this.fetchLocations();
        
        // Refresh the store preview
        this.refreshStorePreview();
        
        this.$snackbar.show(
          `Successfully disabled appointments for ${allLocations.length} location${allLocations.length > 1 ? 's' : ''}!`, 
          'success'
        );
        
      } catch (error) {
        console.error('Error disabling appointments for all locations:', error);
        this.$snackbar.show('Error disabling appointments for all locations. Please try again.', 'error');
      } finally {
        this.isDisablingAllAppointments = false;
      }
    },
    async loadSubdomainData() {
      console.log('🔍 [loadSubdomainData] Loading subdomain data for locationId:', this.selectedLocationId);
      if (!this.selectedLocationId) {
        console.log('❌ [loadSubdomainData] No selectedLocationId');
        this.currentSubdomain = null;
        return;
      }
      try {
        const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.selectedLocationId);
        const locationDoc = await getDoc(locationRef);
        if (locationDoc.exists()) {
          const locationData = locationDoc.data();
          this.currentSubdomain = locationData.subdomain || null;
          console.log('✅ [loadSubdomainData] Loaded subdomain:', this.currentSubdomain, 'from location data:', locationData);
        } else {
          console.log('❌ [loadSubdomainData] Location document does not exist');
          this.currentSubdomain = null;
        }
      } catch (error) {
        console.error('❌ [loadSubdomainData] Error loading subdomain data:', error);
        this.currentSubdomain = null;
      }
    },
    validateSubdomain(subdomain) {
      if (!subdomain) {
        return 'Subdomain is required';
      }
      if (subdomain.length < 3) {
        return 'Subdomain must be at least 3 characters long';
      }
      if (subdomain.length > 63) {
        return 'Subdomain must be less than 63 characters';
      }
      if (!/^[a-z0-9-]+$/.test(subdomain)) {
        return 'Subdomain can only contain lowercase letters, numbers, and hyphens';
      }
      if (subdomain.startsWith('-') || subdomain.endsWith('-')) {
        return 'Subdomain cannot start or end with a hyphen';
      }
      if (subdomain.includes('--')) {
        return 'Subdomain cannot contain consecutive hyphens';
      }
      return null;
    },
    async checkSubdomainAvailability(subdomain) {
      console.log('🔍 [checkSubdomainAvailability] Starting availability check for subdomain:', subdomain);
      if (!subdomain) {
        console.log('❌ [checkSubdomainAvailability] No subdomain provided');
        this.subdomainAvailability = null;
        return;
      }
      const error = this.validateSubdomain(subdomain);
      if (error) {
        console.log('❌ [checkSubdomainAvailability] Validation error:', error);
        this.subdomainError = error;
        this.subdomainAvailability = 'unavailable';
        return;
      }
      this.subdomainError = '';
      this.subdomainAvailability = 'checking';
      console.log('🔍 [checkSubdomainAvailability] Checking availability in database...');
      try {
        const domainRef = doc(db, 'unique_domain', subdomain);
        const domainDoc = await getDoc(domainRef);
        if (domainDoc.exists()) {
          const domainData = domainDoc.data();
          console.log('🔍 [checkSubdomainAvailability] Domain exists, checking ownership:', {
            domainData,
            currentUID: this.currentUID,
            WID: this.WID,
            selectedLocationId: this.selectedLocationId
          });
          // Check if this subdomain belongs to the current user, workspace, and location
          if (
            domainData.uid === this.currentUID &&
            domainData.wid === this.WID &&
            domainData.locationId === this.selectedLocationId &&
            domainData.type === 'individual_store'
          ) {
            // This subdomain belongs to the current user/location, so it's available for editing
            console.log('✅ [checkSubdomainAvailability] Subdomain belongs to current user/location');
            this.subdomainAvailability = 'available';
          } else {
            // This subdomain belongs to someone else or different workspace/location
            console.log('❌ [checkSubdomainAvailability] Subdomain belongs to different user/workspace/location');
            this.subdomainAvailability = 'unavailable';
          }
        } else {
          // Subdomain doesn't exist, so it's available for creation
          console.log('✅ [checkSubdomainAvailability] Subdomain is available for creation');
          this.subdomainAvailability = 'available';
        }
      } catch (error) {
        console.error('❌ [checkSubdomainAvailability] Error checking subdomain availability:', error);
        this.subdomainAvailability = 'unavailable';
      }
      console.log('🔍 [checkSubdomainAvailability] Finished availability check. Final status:', this.subdomainAvailability);
    },
    async saveSubdomain() {
      console.log('🔍 [saveSubdomain] Attempting to save subdomain:', this.newSubdomain);
      if (!this.newSubdomain || this.subdomainError || this.subdomainAvailability !== 'available') {
        console.log('❌ [saveSubdomain] Validation failed:', {
          noSubdomain: !this.newSubdomain,
          hasError: !!this.subdomainError,
          availability: this.subdomainAvailability
        });
        return;
      }
      this.isSavingSubdomain = true;
      try {
        const oldSubdomain = this.currentSubdomain;
        const newSubdomain = this.newSubdomain.toLowerCase().trim();
        const validationError = this.validateSubdomain(newSubdomain);
        if (validationError) {
          console.log('❌ [saveSubdomain] Validation error:', validationError);
          this.subdomainError = validationError;
          this.subdomainAvailability = 'unavailable';
          return;
        }
        
        // If availability hasn't been checked yet, check it now
        if (!this.subdomainAvailability) {
          console.log('🔍 [saveSubdomain] Checking availability before saving...');
          await this.checkSubdomainAvailability(newSubdomain);
          if (this.subdomainAvailability !== 'available') {
            console.log('❌ [saveSubdomain] Subdomain not available after check');
            return;
          }
        }
        // Double-check uniqueness in unique_domain collection before saving
        if (newSubdomain !== oldSubdomain) {
          const domainRef = doc(db, 'unique_domain', newSubdomain);
          const domainDoc = await getDoc(domainRef);
          
          if (domainDoc.exists()) {
            const domainData = domainDoc.data();
                      if (domainData.uid !== this.currentUID || domainData.wid !== this.WID || domainData.locationId !== this.selectedLocationId || domainData.type !== 'individual_store') {
            this.subdomainError = 'This subdomain is already taken by another workspace or store';
            this.subdomainAvailability = 'unavailable';
            return;
          }
          }
        }
        
        // Update location with new subdomain
        const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.selectedLocationId);
        await updateDoc(locationRef, {
          subdomain: newSubdomain
        });
        
        // If there was an old subdomain and it's different from the new one, delete it from unique_domain collection
        if (oldSubdomain && oldSubdomain !== newSubdomain) {
          const oldDomainRef = doc(db, 'unique_domain', oldSubdomain);
          await deleteDoc(oldDomainRef);
        }
        
        // Create new subdomain document in unique_domain collection
        const newDomainRef = doc(db, 'unique_domain', newSubdomain);
        await setDoc(newDomainRef, {
          uid: this.currentUID,
          wid: this.WID,
          locationId: this.selectedLocationId,
          type: 'individual_store',
          created_at: new Date(),
          updated_at: new Date()
        });
        // Update current subdomain
        this.currentSubdomain = newSubdomain;
        // Close dialog and reset form
        this.showSubdomainEditDialog = false;
        this.newSubdomain = '';
        this.subdomainError = '';
        this.subdomainAvailability = null;
        this.$snackbar.show(`Subdomain ${oldSubdomain ? 'updated' : 'created'} successfully! Your landing page is now accessible at ${this.getSubdomainUrl(newSubdomain, 'landing')}`, 'success');
      } catch (error) {
        console.error('Error saving subdomain:', error);
        this.$snackbar.show('Error updating subdomain. Please try again.', 'error');
      } finally {
        this.isSavingSubdomain = false;
      }
    },
    openSubdomainDialog() {
      console.log('🔍 [openSubdomainDialog] Opening dialog with currentSubdomain:', this.currentSubdomain);
      
      // For new subdomains, don't set newSubdomain initially - let user type
      // For existing subdomains, pre-populate with current value
      if (this.currentSubdomain) {
        this.newSubdomain = this.currentSubdomain;
      } else {
        this.newSubdomain = ''; // Start empty for new subdomains
      }
      
      this.subdomainError = '';
      this.subdomainAvailability = null;
      this.showSubdomainEditDialog = true;
      
      console.log('🔍 [openSubdomainDialog] Set newSubdomain to:', this.newSubdomain);
      
      // Focus and select the input field after dialog opens
      this.$nextTick(() => {
        if (this.$refs.subdomainInput) {
          this.$refs.subdomainInput.focus();
        }
        
        // If there's an existing subdomain, it should be available for editing
        if (this.newSubdomain && this.currentSubdomain) {
          // For existing subdomains, we know it belongs to the current user/location
          console.log('✅ [openSubdomainDialog] Existing subdomain, setting availability to available');
          this.subdomainAvailability = 'available';
        } else if (this.newSubdomain) {
          // For new subdomains, check availability
          console.log('🔍 [openSubdomainDialog] New subdomain, checking availability');
          this.checkSubdomainAvailability(this.newSubdomain);
        } else {
          console.log('⚠️ [openSubdomainDialog] No subdomain provided - this is normal for new subdomains');
          // For new subdomains, the button will be enabled once user starts typing
        }
      });
    },
    closeSubdomainDialog() {
      this.showSubdomainEditDialog = false;
      this.newSubdomain = '';
      this.subdomainError = '';
      this.subdomainAvailability = null;
    },
    
    // Custom domain methods
    async loadCustomDomainData() {
      if (!this.currentUID || !this.selectedLocationId) {
        return;
      }
      
      try {
        const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.selectedLocationId);
        const locationDoc = await getDoc(locationRef);
        
        if (locationDoc.exists()) {
          const locationData = locationDoc.data();
          this.currentCustomDomain = locationData.custom_domain || null;
        }
      } catch (error) {
        console.error('Error loading custom domain data:', error);
        this.currentCustomDomain = null;
      }
    },
    
    validateCustomDomain(domain) {
      if (!domain) {
        return 'Domain is required';
      }
      
      // Basic domain validation
      const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      
      if (!domainRegex.test(domain)) {
        return 'Invalid domain format';
      }
      
      if (domain.length > 253) {
        return 'Domain is too long';
      }
      
      // Check if it's a subdomain format
      const parts = domain.split('.');
      if (parts.length < 2) {
        return 'Domain must have at least one subdomain and TLD';
      }
      
      return null;
    },
    
    async checkCustomDomainAvailability(domain) {
      if (!domain) {
        this.customDomainAvailability = null;
        return;
      }
      
      const error = this.validateCustomDomain(domain);
      if (error) {
        this.customDomainError = error;
        this.customDomainAvailability = 'unavailable';
        return;
      }
      
      this.customDomainError = '';
      this.customDomainAvailability = 'checking';
      
      try {
        // TODO: Implement actual DNS verification functionality
        // Currently only validating domain format
        // Future enhancement: Verify DNS records are properly configured
        // and pointing to the correct target before allowing domain connection
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate check
        
        this.customDomainAvailability = 'available';
      } catch (error) {
        console.error('Error checking custom domain availability:', error);
        this.customDomainAvailability = 'unavailable';
      }
    },
    
    async saveCustomDomain() {
      if (!this.newCustomDomain || this.customDomainError || this.customDomainAvailability !== 'available') {
        return;
      }
      
      this.isSavingCustomDomain = true;
      
      try {
        const oldCustomDomain = this.currentCustomDomain;
        const newCustomDomain = this.newCustomDomain.toLowerCase().trim();
        
        // Validate the domain one more time
        const validationError = this.validateCustomDomain(newCustomDomain);
        if (validationError) {
          this.customDomainError = validationError;
          this.customDomainAvailability = 'unavailable';
          return;
        }
        
        // Update location with new custom domain
        const locationRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.selectedLocationId);
        await updateDoc(locationRef, {
          custom_domain: newCustomDomain
        });
        
        // Update current custom domain
        this.currentCustomDomain = newCustomDomain;
        
        // Close dialog and reset form
        this.showCustomDomainDialog = false;
        this.newCustomDomain = '';
        this.customDomainError = '';
        this.customDomainAvailability = null;
        
        this.$snackbar.show(`Custom domain ${oldCustomDomain ? 'updated' : 'connected'} successfully! Please configure your DNS settings as shown in the instructions. Note: DNS verification will be implemented in a future update.`, 'success');
      } catch (error) {
        console.error('Error saving custom domain:', error);
        this.$snackbar.show('Error updating custom domain. Please try again.', 'error');
      } finally {
        this.isSavingCustomDomain = false;
      }
    },
    
    openCustomDomainDialog() {
      this.newCustomDomain = this.currentCustomDomain || '';
      this.customDomainError = '';
      this.customDomainAvailability = null;
      this.showCustomDomainDialog = true;
      
      // Focus and select the input field after dialog opens
      this.$nextTick(() => {
        if (this.$refs.customDomainInput) {
          this.$refs.customDomainInput.focus();
        }
        
        // Check availability after a short delay
        if (this.newCustomDomain) {
          this.checkCustomDomainAvailability(this.newCustomDomain);
        }
      });
    },
    
    closeCustomDomainDialog() {
      this.showCustomDomainDialog = false;
      this.newCustomDomain = '';
      this.customDomainError = '';
      this.customDomainAvailability = null;
    },
    
    // Helper methods for custom domain
    getDomainParts(domain) {
      if (!domain) return null;
      const parts = domain.split('.');
      if (parts.length < 2) return null;
      return {
        subdomain: parts[0],
        domain: parts.slice(1).join('.')
      };
    },
    
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$snackbar.show('Copied to clipboard!', 'success');
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        this.$snackbar.show('Failed to copy to clipboard', 'error');
      }
    },
    
    forwardInstructions() {
      const instructions = `DNS Configuration for ${this.newCustomDomain || 'your domain'}:

CNAME Record:
- HOST NAME: ${this.getDomainParts(this.newCustomDomain || 'landing.yourdomain.com')?.subdomain || 'landing'}
- REQUIRED VALUE: ${this.getCustomDomainCnameTarget(this.currentSubdomain || 'your-subdomain', 'landing')}

Note: The changes may take up to 48 hours to reflect.`;
      
      // Create a shareable link or copy instructions
      if (navigator.share) {
        navigator.share({
          title: 'DNS Configuration Instructions',
          text: instructions
        });
      } else {
        this.copyToClipboard(instructions);
      }
    },
    
    // Helper method to use the imported function
    getCustomDomainCnameTarget(subdomain, type) {
      return getCustomDomainCnameTarget(subdomain, type);
    },
    debugButtonState() {
      console.log('🔍 [debugButtonState] Button state debug:', {
        newSubdomain: this.newSubdomain,
        subdomainError: this.subdomainError,
        subdomainAvailability: this.subdomainAvailability,
        isSavingSubdomain: this.isSavingSubdomain,
        disabled: !this.newSubdomain || this.subdomainError || this.subdomainAvailability !== 'available' || this.isSavingSubdomain
      });
    },
    getSocialPlatformColor(platform) {
      const colors = {
        facebook: 'blue',
        instagram: 'purple',
        twitter: 'blue-darken-1',
        youtube: 'red',
        threads: 'grey-darken-1',
        linkedin: 'blue-darken-2',
        tiktok: 'black',
        whatsapp: 'green',
      };
      return colors[platform] || 'grey';
    },
    getSocialPlatformIcon(platform) {
      const icons = {
        facebook: 'mdi-facebook',
        instagram: 'mdi-instagram',
        twitter: 'mdi-twitter',
        youtube: 'mdi-youtube',
        threads: 'mdi-message-text',
        linkedin: 'mdi-linkedin',
        tiktok: 'mdi-music-note',
        whatsapp: 'mdi-whatsapp',
      };
      return icons[platform] || 'mdi-link';
    },
    getSocialPlatformName(platform) {
      const names = {
        facebook: 'Facebook',
        instagram: 'Instagram',
        twitter: 'Twitter',
        youtube: 'YouTube',
        threads: 'Threads',
        linkedin: 'LinkedIn',
        tiktok: 'TikTok',
        whatsapp: 'WhatsApp',
      };
      return names[platform] || platform;
    },
    
    // Products & Services Methods
    addNewProductService() {
      this.productsServicesDraft.push({
        name: '',
        description: '',
        unitPrice: '',
        images: [''],
        videos: ['']
      });
    },
    
    removeProductService(index) {
      // Store the product to delete and show confirmation dialog
      this.productToDelete = this.productsServicesDraft[index];
      this.showDeleteProductDialog = true;
    },

    confirmDeleteProduct() {
      // Find the index of the product to delete
      const index = this.productsServicesDraft.findIndex(product => product === this.productToDelete);
      if (index !== -1) {
        this.productsServicesDraft.splice(index, 1);
      }
      // Close dialog and clear the product reference
      this.showDeleteProductDialog = false;
      this.productToDelete = null;
    },
    
    addImage(productIndex) {
      this.productsServicesDraft[productIndex].images.push('');
    },
    
    removeImage(productIndex, imageIndex) {
      this.productsServicesDraft[productIndex].images.splice(imageIndex, 1);
      // Ensure there's always at least one image field
      if (this.productsServicesDraft[productIndex].images.length === 0) {
        this.productsServicesDraft[productIndex].images.push('');
      }
    },
    
    addVideo(productIndex) {
      this.productsServicesDraft[productIndex].videos.push('');
    },
    
    removeVideo(productIndex, videoIndex) {
      this.productsServicesDraft[productIndex].videos.splice(videoIndex, 1);
      // Ensure there's always at least one video field
      if (this.productsServicesDraft[productIndex].videos.length === 0) {
        this.productsServicesDraft[productIndex].videos.push('');
      }
    },

    // CSV Import/Export Methods
    handleCsvImport(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csvContent = e.target.result;
          const products = this.parseCsvContent(csvContent);
          
          if (products.length > 0) {
            // Append new products to existing ones instead of replacing
            const existingCount = this.productsServicesDraft.length;
            this.productsServicesDraft = [...this.productsServicesDraft, ...products];
            
            this.csvImportStatus = {
              type: 'success',
              message: `Successfully imported ${products.length} product(s)/service(s). Total: ${this.productsServicesDraft.length}`
            };
          } else {
            this.csvImportStatus = {
              type: 'error',
              message: 'No valid products found in CSV file'
            };
          }
        } catch (error) {
          console.error('CSV import error:', error);
          this.csvImportStatus = {
            type: 'error',
            message: 'Error parsing CSV file. Please check the format.'
          };
        }
        
        // Clear the file input
        event.target.value = '';
        
        // Clear status after 5 seconds
        setTimeout(() => {
          this.csvImportStatus = null;
        }, 5000);
      };
      
      reader.readAsText(file);
    },

    parseCsvContent(csvContent) {
      const lines = csvContent.split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      const products = [];
      
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        const values = this.parseCsvLine(line);
        if (values.length < headers.length) continue;
        
        const product = {
          name: values[headers.indexOf('name')] || '',
          description: values[headers.indexOf('description')] || '',
          unitPrice: values[headers.indexOf('unitprice')] || '',
          images: [],
          videos: []
        };
        
        // Parse images (comma-separated URLs)
        const imagesStr = values[headers.indexOf('images')] || '';
        if (imagesStr) {
          product.images = imagesStr.split('|').map(url => url.trim()).filter(url => url);
        }
        if (product.images.length === 0) {
          product.images = [''];
        }
        
        // Parse videos (comma-separated URLs)
        const videosStr = values[headers.indexOf('videos')] || '';
        if (videosStr) {
          product.videos = videosStr.split('|').map(url => url.trim()).filter(url => url);
        }
        if (product.videos.length === 0) {
          product.videos = [''];
        }
        
        // Only add if name is provided
        if (product.name.trim()) {
          products.push(product);
        }
      }
      
      return products;
    },

    parseCsvLine(line) {
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      
      values.push(current.trim());
      return values;
    },

    exportToCsv() {
      if (this.productsServicesDraft.length === 0) {
        this.csvImportStatus = {
          type: 'error',
          message: 'No products to export'
        };
        return;
      }
      
      const headers = ['name', 'description', 'unitPrice', 'images', 'videos'];
      const csvContent = [
        headers.join(','),
        ...this.productsServicesDraft.map(product => {
          const images = product.images.filter(img => img.trim()).join('|');
          const videos = product.videos.filter(vid => vid.trim()).join('|');
          return [
            `"${product.name || ''}"`,
            `"${product.description || ''}"`,
            `"${product.unitPrice || ''}"`,
            `"${images}"`,
            `"${videos}"`
          ].join(',');
        })
      ].join('\n');
      
      this.downloadCsvFile(csvContent, 'products_services_export.csv');
      
      this.csvImportStatus = {
        type: 'success',
        message: 'CSV exported successfully'
      };
      
      setTimeout(() => {
        this.csvImportStatus = null;
      }, 3000);
    },

    downloadCsvTemplate() {
      const template = `name,description,unitPrice,images,videos
"Sample Product","This is a sample product description","$50","https://example.com/image1.jpg|https://example.com/image2.jpg","https://youtube.com/watch?v=example|https://example.com/video.mp4"
"Sample Service","This is a sample service description","$100/hour","https://example.com/service-image.jpg","https://vimeo.com/example"
"Free Product","This product is free","Free","https://example.com/free-product.jpg",""`;
      
      this.downloadCsvFile(template, 'products_services_template.csv');
      
      this.csvImportStatus = {
        type: 'success',
        message: 'Template downloaded successfully'
      };
      
      setTimeout(() => {
        this.csvImportStatus = null;
      }, 3000);
    },

    downloadCsvFile(content, filename) {
      const blob = new Blob([content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },

    // Drag and Drop Reordering Methods
    handleDragStart(index, event) {
      this.draggedIndex = index;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', event.target.outerHTML);
    },

    handleDrop(targetIndex, event) {
      event.preventDefault();
      if (this.draggedIndex === null || this.draggedIndex === targetIndex) {
        return;
      }

      // Reorder the array
      const items = [...this.productsServicesDraft];
      const draggedItem = items[this.draggedIndex];
      items.splice(this.draggedIndex, 1);
      items.splice(targetIndex, 0, draggedItem);
      
      this.productsServicesDraft = items;
      this.draggedIndex = null;
    },

    moveProductUp(index) {
      if (index > 0) {
        const items = [...this.productsServicesDraft];
        const temp = items[index];
        items[index] = items[index - 1];
        items[index - 1] = temp;
        this.productsServicesDraft = items;
      }
    },

    moveProductDown(index) {
      if (index < this.productsServicesDraft.length - 1) {
        const items = [...this.productsServicesDraft];
        const temp = items[index];
        items[index] = items[index + 1];
        items[index + 1] = temp;
        this.productsServicesDraft = items;
      }
    },

    // Cloudinary Upload Methods
    loadCloudinaryWidget() {
      if (!window.cloudinary) {
        const script = document.createElement('script');
        script.src = "https://upload-widget.cloudinary.com/global/all.js";
        script.onload = () => {
          console.log("Cloudinary widget loaded");
        };
        document.head.appendChild(script);
      }
    },

    initCloudinaryImageUpload(index) {
      if (!window.cloudinary) {
        this.loadCloudinaryWidget();
        setTimeout(() => this.initCloudinaryImageUpload(index), 1000);
        return;
      }

      this.isUploadingImage = true;
      this.currentUploadIndex = index;
      this.currentUploadType = 'image';

      this.cloudinaryWidget = window.cloudinary.createUploadWidget(
        {
          cloudName: process.env.VUE_APP_CLOUDINARY_CLOUD_NAME,
          uploadPreset: "demo_preset",
          sources: [
            "local",
            "camera",
            "url",
            "dropbox",
            "image_search",
            "shutterstock",
            "gettyimages",
            "istock",
            "unsplash",
            "google_drive",
          ],
          accept: "image/*",
          clientAllowedFormats: ["png", "jpg", "jpeg", "heic", "heif", "webp"],
          multiple: false,
          maxImageFileSize: 20000000,
          tags: [`${this.currentUID}_${this.WID}`],
          folder: `users/${this.currentUID}/${this.WID}/products_services/images`,
        },
        (error, result) => {
          if (!error && result && result.event === "success") {
            this.handleImageUpload(result.info.secure_url);
          }
          this.isUploadingImage = false;
        }
      );

      this.cloudinaryWidget.open();
    },

    initCloudinaryVideoUpload(index) {
      console.log('🚀 [initCloudinaryVideoUpload] Starting video upload for index:', index);
      
      if (!window.cloudinary) {
        console.log('⚠️ [initCloudinaryVideoUpload] Cloudinary not loaded, loading widget...');
        this.loadCloudinaryWidget();
        setTimeout(() => this.initCloudinaryVideoUpload(index), 1000);
        return;
      }

      this.isUploadingVideo = true;
      this.currentUploadIndex = index;
      this.currentUploadType = 'video';

      console.log('📋 [initCloudinaryVideoUpload] Widget configuration:', {
        cloudName: process.env.VUE_APP_CLOUDINARY_CLOUD_NAME,
        uploadPreset: "demo_preset",
        accept: "video/*",
        clientAllowedFormats: ["mp4", "mov", "avi", "mkv", "webm"],
        maxVideoFileSize: 100000000
      });

      this.cloudinaryWidget = window.cloudinary.createUploadWidget(
        {
          cloudName: process.env.VUE_APP_CLOUDINARY_CLOUD_NAME,
          uploadPreset: "demo_preset",
          sources: [
            "local",
            "camera",
            "url",
            "dropbox",
            "google_drive",
          ],
          accept: "video/*",
          clientAllowedFormats: ["mp4", "mov", "avi", "mkv", "webm"],
          multiple: false,
          maxVideoFileSize: 100000000, // 100MB
          tags: [`${this.currentUID}_${this.WID}`],
          folder: `users/${this.currentUID}/${this.WID}/products_services/videos`,
          // Remove resourceType and transformation to avoid conflicts
        },
        (error, result) => {
          console.log('📡 [Cloudinary Callback] Error:', error);
          console.log('📡 [Cloudinary Callback] Result:', result);
          
          if (!error && result && result.event === "success") {
            console.log('✅ [Cloudinary Callback] Upload successful:', result.info.secure_url);
            this.handleVideoUpload(result.info.secure_url);
          } else if (error) {
            console.error('❌ [Cloudinary Callback] Upload error:', error);
            console.error('❌ [Cloudinary Callback] Error details:', {
              status: error.status,
              statusText: error.statusText,
              message: error.message,
              data: error.data
            });
            this.$snackbar.show(`Error uploading video: ${error.statusText || error.message}`, 'error');
          }
          this.isUploadingVideo = false;
        }
      );

      console.log('🎬 [initCloudinaryVideoUpload] Opening Cloudinary widget...');
      this.cloudinaryWidget.open();
    },

    handleImageUpload(imageUrl) {
      if (this.currentUploadIndex !== null && this.productsServicesDraft[this.currentUploadIndex]) {
        // Add the new image URL to the images array
        if (!this.productsServicesDraft[this.currentUploadIndex].images) {
          this.productsServicesDraft[this.currentUploadIndex].images = [];
        }
        this.productsServicesDraft[this.currentUploadIndex].images.push(imageUrl);
        this.currentUploadIndex = null;
        this.currentUploadType = null;
      }
    },

    handleVideoUpload(videoUrl) {
      if (this.currentUploadIndex !== null && this.productsServicesDraft[this.currentUploadIndex]) {
        // Add the new video URL to the videos array
        if (!this.productsServicesDraft[this.currentUploadIndex].videos) {
          this.productsServicesDraft[this.currentUploadIndex].videos = [];
        }
        this.productsServicesDraft[this.currentUploadIndex].videos.push(videoUrl);
        this.currentUploadIndex = null;
        this.currentUploadType = null;
      }
    },

    // Advanced video upload with Cloudinary configuration and transformations
    async uploadVideoToCloudinary(videoFile, index) {
      try {
        // Import required functions
        const { accessSecretFlySecrets } = await import('@/utils/firebase.utils');
        const cloudinary = require('cloudinary').v2;
        
        // Get Cloudinary secrets
        const secrets = await accessSecretFlySecrets();
        cloudinary.config({
          cloud_name: secrets.cloudinary.cloud_name,
          api_key: secrets.cloudinary.api_key,
          api_secret: secrets.cloudinary.secret_key,
        });

        // Upload video to Cloudinary
        const cloudinaryResult = await cloudinary.uploader.upload(videoFile, {
          resource_type: 'video',
          folder: `users/${this.currentUID}/${this.WID}/products_services/videos`,
          tags: [`${this.currentUID}_${this.WID}`],
        });

        const uploadedUrl = cloudinaryResult.url;
        
        // Generate optimized video URL with bitrate transformation
        const cloudinaryBaseUrl = `https://res.cloudinary.com/${secrets.cloudinary.cloud_name}/video/upload`;
        const transformation = 'br_24m';
        const extractedUrl = this.extractPublicId(uploadedUrl);
        const optimizedUrl = `${cloudinaryBaseUrl}/${transformation}/${extractedUrl}`;

        // Add both original and optimized URLs
        if (!this.productsServicesDraft[index].videos) {
          this.productsServicesDraft[index].videos = [];
        }
        this.productsServicesDraft[index].videos.push(optimizedUrl);
        
        return optimizedUrl;
      } catch (error) {
        console.error('Error uploading video to Cloudinary:', error);
        this.$snackbar.show('Error uploading video. Please try again.', 'error');
        throw error;
      }
    },

    // Simplified video upload method using Cloudinary widget
    async uploadVideoToCloudinaryWidget(index) {
      console.log('🚀 [uploadVideoToCloudinaryWidget] Starting widget upload for index:', index);
      
      this.currentUploadIndex = index;
      this.currentUploadType = 'video';
      this.isUploadingVideo = true;

      // Load Cloudinary widget if not already loaded
      if (!window.cloudinary) {
        console.log('📦 [uploadVideoToCloudinaryWidget] Loading Cloudinary widget...');
        this.loadCloudinaryWidget();
        setTimeout(() => this.uploadVideoToCloudinaryWidget(index), 1000);
        return;
      }

      console.log('📋 [uploadVideoToCloudinaryWidget] Creating upload widget...');
      
      try {
        // Use vid_preset for video uploads
        console.log('📋 [uploadVideoToCloudinaryWidget] Widget config:', {
          cloudName: process.env.VUE_APP_CLOUDINARY_CLOUD_NAME,
          uploadPreset: "vid_preset",
          sources: ["local"],
          multiple: false
        });
        
        this.cloudinaryWidget = window.cloudinary.createUploadWidget(
          {
            cloudName: process.env.VUE_APP_CLOUDINARY_CLOUD_NAME,
            uploadPreset: "vid_preset",
            sources: ["local"],
            multiple: false,
            // Use vid_preset specifically for videos
          },
                      (error, result) => {
              console.log('📡 [Cloudinary Widget Callback] Error:', error);
              console.log('📡 [Cloudinary Widget Callback] Result:', result);
              console.log('📡 [Cloudinary Widget Callback] Result info:', result?.info);
              console.log('📡 [Cloudinary Widget Callback] Result event:', result?.event);
              
              if (!error && result && result.event === "success") {
                console.log('✅ [Cloudinary Widget Callback] Upload successful:', result.info.secure_url);
                this.handleVideoUpload(result.info.secure_url);
              } else if (error) {
                console.error('❌ [Cloudinary Widget Callback] Upload error:', error);
                console.error('❌ [Cloudinary Widget Callback] Error details:', {
                  status: error.status,
                  statusText: error.statusText,
                  message: error.message,
                  data: error.data
                });
                this.$snackbar.show(`Error uploading video: ${error.statusText || error.message}`, 'error');
              }
              this.isUploadingVideo = false;
            }
        );

        console.log('🎬 [uploadVideoToCloudinaryWidget] Opening widget...');
        this.cloudinaryWidget.open();
        
      } catch (error) {
        console.error('❌ [uploadVideoToCloudinaryWidget] Error creating widget:', error);
        this.$snackbar.show('Error initializing video upload. Please try again.', 'error');
        this.isUploadingVideo = false;
      }
    },

    // Extract public ID from Cloudinary URL
    extractPublicId(url) {
      if (!url || !url.includes('cloudinary.com')) return null;
      
      const urlParts = url.split('/');
      const uploadIndex = urlParts.findIndex(part => part === 'upload');
      if (uploadIndex === -1) return null;
      
      // Get everything after 'upload' and before the file extension
      const publicIdParts = urlParts.slice(uploadIndex + 1);
      const publicId = publicIdParts.join('/');
      
      // Remove file extension
      return publicId.replace(/\.[^/.]+$/, '');
    },

    // Save products and services data to Firebase
    async saveProductsServicesToFirebase() {
      try {
        if (!this.currentUID || !this.WID) {
          console.error('Missing required data for saving products and services');
          return;
        }

        const workspaceRef = doc(db, 'users', this.currentUID, 'fly_workspaces', this.WID);
        
        // Update the workspace document with products and services data
        await updateDoc(workspaceRef, {
          products_services: this.productsServicesDraft,
          updated_at: new Date()
        });

        console.log('✅ Products and services saved to Firebase successfully');
        this.$snackbar.show('Products and services saved successfully!', 'success');
        
        // Update the original data for change tracking
        this.originalProductsServices = JSON.parse(JSON.stringify(this.productsServicesDraft));
        
      } catch (error) {
        console.error('❌ Error saving products and services to Firebase:', error);
        this.$snackbar.show('Error saving products and services. Please try again.', 'error');
      }
    },

    // Auto-save after upload
    async handleImageUpload(imageUrl) {
      if (this.currentUploadIndex !== null && this.productsServicesDraft[this.currentUploadIndex]) {
        // Add the new image URL to the images array
        if (!this.productsServicesDraft[this.currentUploadIndex].images) {
          this.productsServicesDraft[this.currentUploadIndex].images = [];
        }
        this.productsServicesDraft[this.currentUploadIndex].images.push(imageUrl);
        
        // Save to Firebase
        await this.saveProductsServicesToFirebase();
        
        this.currentUploadIndex = null;
        this.currentUploadType = null;
      }
    },

    // Auto-save after video upload
    async handleVideoUpload(videoUrl) {
      if (this.currentUploadIndex !== null && this.productsServicesDraft[this.currentUploadIndex]) {
        // Add the new video URL to the videos array
        if (!this.productsServicesDraft[this.currentUploadIndex].videos) {
          this.productsServicesDraft[this.currentUploadIndex].videos = [];
        }
        this.productsServicesDraft[this.currentUploadIndex].videos.push(videoUrl);
        
        // Save to Firebase
        await this.saveProductsServicesToFirebase();
        
        this.currentUploadIndex = null;
        this.currentUploadType = null;
      }
    },

    // Process video URLs through Cloudinary (simplified version)
    async processVideoThroughCloudinary(videoUrl, index) {
      try {
        if (!videoUrl) return;
        
        console.log('🔄 [processVideoThroughCloudinary] Processing video URL:', videoUrl);
        
        // For now, just save the URL as-is since we can't use server-side Cloudinary
        // In the future, this could be implemented with a Firebase function
        console.log('✅ [processVideoThroughCloudinary] Video URL saved as-is');
        
        // Save to Firebase
        await this.saveProductsServicesToFirebase();
        
        return videoUrl;
      } catch (error) {
        console.error('❌ [processVideoThroughCloudinary] Error processing video:', error);
        this.$snackbar.show('Error processing video. Please try again.', 'error');
        throw error;
      }
    },

    // File-based video upload (alternative method)
    initFileVideoUpload(index) {
      console.log('🚀 [initFileVideoUpload] Starting file-based video upload for index:', index);
      
      this.currentUploadIndex = index;
      this.currentUploadType = 'video';
      this.isUploadingVideo = true;
      
      // Debug: Log all refs to see what's available
      console.log('🔍 [initFileVideoUpload] All refs:', Object.keys(this.$refs));
      console.log('🔍 [initFileVideoUpload] Looking for ref:', `videoFileInput_${index}`);
      
      // Try multiple approaches to find the file input
      this.$nextTick(() => {
        // Method 1: Try dynamic ref
        let fileInput = this.$refs[`videoFileInput_${index}`];
        console.log('📁 [initFileVideoUpload] Method 1 - Dynamic ref result:', fileInput);
        
        // Method 2: If dynamic ref fails, try querySelector
        if (!fileInput || typeof fileInput.click !== 'function') {
          console.log('📁 [initFileVideoUpload] Trying querySelector method...');
          fileInput = this.$el.querySelector(`input[data-index="${index}"][type="file"]`);
          console.log('📁 [initFileVideoUpload] Method 2 - QuerySelector result:', fileInput);
        }
        
        // Method 3: If still not found, try finding by accept attribute
        if (!fileInput || typeof fileInput.click !== 'function') {
          console.log('📁 [initFileVideoUpload] Trying accept attribute method...');
          const allFileInputs = this.$el.querySelectorAll('input[type="file"][accept="video/*"]');
          console.log('📁 [initFileVideoUpload] All video file inputs found:', allFileInputs.length);
          
          // Find the one that belongs to this index
          for (let i = 0; i < allFileInputs.length; i++) {
            const input = allFileInputs[i];
            if (input.getAttribute('data-index') === index.toString()) {
              fileInput = input;
              console.log('📁 [initFileVideoUpload] Found file input by data-index:', fileInput);
              break;
            }
          }
        }
        
        console.log('📁 [initFileVideoUpload] Final file input:', fileInput);
        console.log('📁 [initFileVideoUpload] File input type:', typeof fileInput);
        console.log('📁 [initFileVideoUpload] File input click method:', typeof fileInput?.click);
        
        if (fileInput && typeof fileInput.click === 'function') {
          console.log('📁 [initFileVideoUpload] Triggering file input...');
          fileInput.click();
        } else {
          console.error('❌ [initFileVideoUpload] File input not found or click method not available');
          console.error('❌ [initFileVideoUpload] File input:', fileInput);
          this.isUploadingVideo = false;
        }
      });
    },

    async handleVideoFileUpload(event, index) {
      console.log('🚀 [handleVideoFileUpload] Starting file upload for index:', index);
      
      try {
        const file = event.target.files[0];
        console.log('📁 [handleVideoFileUpload] Selected file:', {
          name: file?.name,
          type: file?.type,
          size: file?.size,
          lastModified: file?.lastModified
        });
        
        if (!file) {
          console.log('⚠️ [handleVideoFileUpload] No file selected');
          this.isUploadingVideo = false;
          return;
        }

        // Validate file type
        const allowedTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/mkv', 'video/webm'];
        console.log('🔍 [handleVideoFileUpload] File type validation:', {
          fileType: file.type,
          allowedTypes: allowedTypes,
          isValid: allowedTypes.includes(file.type)
        });
        
        if (!allowedTypes.includes(file.type)) {
          console.error('❌ [handleVideoFileUpload] Invalid file type:', file.type);
          this.$snackbar.show('Please select a valid video file (MP4, MOV, AVI, MKV, WebM)', 'error');
          this.isUploadingVideo = false;
          return;
        }

        // Validate file size (100MB limit)
        const maxSize = 100 * 1024 * 1024; // 100MB
        console.log('📏 [handleVideoFileUpload] File size validation:', {
          fileSize: file.size,
          maxSize: maxSize,
          isValid: file.size <= maxSize
        });
        
        if (file.size > maxSize) {
          console.error('❌ [handleVideoFileUpload] File too large:', file.size);
          this.$snackbar.show('Video file size must be less than 100MB', 'error');
          this.isUploadingVideo = false;
          return;
        }

        console.log('✅ [handleVideoFileUpload] File validation passed, using Cloudinary widget...');
        
        // Use Cloudinary widget for upload instead of server-side approach
        this.currentUploadIndex = index;
        this.currentUploadType = 'video';
        this.isUploadingVideo = true;
        
        // Load Cloudinary widget if not already loaded
        if (!window.cloudinary) {
          console.log('📦 [handleVideoFileUpload] Loading Cloudinary widget...');
          this.loadCloudinaryWidget();
          setTimeout(() => this.handleVideoFileUpload(event, index), 1000);
          return;
        }

        console.log('📋 [handleVideoFileUpload] Creating upload widget...');
        
        try {
          // Use vid_preset for file upload method too
          this.cloudinaryWidget = window.cloudinary.createUploadWidget(
            {
              cloudName: process.env.VUE_APP_CLOUDINARY_CLOUD_NAME,
              uploadPreset: "vid_preset",
              sources: ["local"],
              multiple: false,
              // Use vid_preset for video uploads
            },
            (error, result) => {
              console.log('📡 [File Upload Widget Callback] Error:', error);
              console.log('📡 [File Upload Widget Callback] Result:', result);
              
              if (!error && result && result.event === "success") {
                console.log('✅ [File Upload Widget Callback] Upload successful:', result.info.secure_url);
                this.handleVideoUpload(result.info.secure_url);
              } else if (error) {
                console.error('❌ [File Upload Widget Callback] Upload error:', error);
                this.$snackbar.show(`Error uploading video: ${error.statusText || error.message}`, 'error');
              }
              this.isUploadingVideo = false;
            }
          );

          console.log('🎬 [handleVideoFileUpload] Opening widget...');
          this.cloudinaryWidget.open();
          
        } catch (error) {
          console.error('❌ [handleVideoFileUpload] Error creating widget:', error);
          this.$snackbar.show('Error initializing video upload. Please try again.', 'error');
          this.isUploadingVideo = false;
        }
        
      } catch (error) {
        console.error('❌ [handleVideoFileUpload] Error uploading video file:', error);
        console.error('❌ [handleVideoFileUpload] Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
        this.$snackbar.show('Error uploading video. Please try again.', 'error');
      } finally {
        console.log('🏁 [handleVideoFileUpload] Cleaning up...');
        this.isUploadingVideo = false;
        this.currentUploadIndex = null;
        this.currentUploadType = null;
        
        // Clear the file input
        event.target.value = '';
      }
    },

    // Convert file to base64
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    },
  },
  beforeDestroy() {
    // Clear any pending timeouts
    if (this.subdomainCheckTimeout) {
      clearTimeout(this.subdomainCheckTimeout);
    }
  }
};
</script>

<style scoped>
/* Add any custom styles for the configurator here */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Hide scrollbar but keep scrolling functionality */
.scrollbar-hide {
  -ms-overflow-style: none;  /* Internet Explorer 10+ */
  scrollbar-width: none;  /* Firefox */
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;  /* Safari and Chrome */
}
</style> 
```
</style> 